// TheClearAudit v1.0.0 — production launch
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { LEVELS } from './data/levels';
import { getQuestionsByLevel } from './data/questions';
import Login from './Login';
import ResetPassword from './ResetPassword';
import { supabase } from './supabase';
import Pricing from './Pricing';
import Profile from './Profile';
import Success from './Success';
import Landing from './Landing';
import Billing from './Billing';
import SPRSScore from './SPRSScore';
// eslint-disable-next-line no-unused-vars
import { calculateSPRSScore, calculateSOC2Score, getSOC2Status, calculateISO27001Score, getISO27001Status, calculateHIPAAScore, getHIPAAStatus, calculateNISTCSFScore, getNISTCSFStatus, calculatePCIDSSScore, getPCIDSSStatus, calculateFedRAMPScore, getFedRAMPStatus, CONTROL_WEIGHTS } from './data/controlWeights';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateSSP, generatePOAM } from './generatePDF';
import { generateSSPDocx } from './generateSSPDocx';
import {
  getProfile, saveProfile,
  getAssessments, saveAnswer,
  getTasks, createTask, updateTask, deleteTask as dbDeleteTask,
  getEvidence, createEvidence, deleteEvidence, updateEvidenceStatus,
  uploadEvidenceFile, getEvidenceDownloadUrl,
  isUserAdmin,
  getCompany, getOrCreateCompany, getCompanyMembers, checkPendingInvite,
  acceptCompanyInvite,
  lookupCompanyInviteByToken, acceptCompanyInviteByToken,
  logActivity,
  logControlChange, getFullControlHistory,
  getNarratives, saveNarrative,
  getShareLinks, createShareLink, updateShareLink, deleteShareLink,
} from './db';
import { getNotifications, markNotificationRead, markAllNotificationsRead, generateSessionNotifications } from './notifications';
import { calculateReadinessScore } from './utils/readinessScore';
import ReadinessGauge from './ReadinessGauge';
import PublicReport from './PublicReport';
import AuditorAccess from './AuditorAccess';
import AuditorView from './AuditorView';
import Affirmation from './Affirmation';
import PolicyLibrary from './PolicyLibrary';
import AIAssistant from './AIAssistant'; // eslint-disable-line no-unused-vars
import SSPReader from './SSPReader';
import FrameworkSelector from './FrameworkSelector';
import { FRAMEWORKS } from './data/frameworks';
import PlanGate, { hasAccess } from './PlanGate';
import AgencyDashboard from './AgencyDashboard';
import AccountSettings from './AccountSettings';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import SecurityPage from './SecurityPage';
import DataProcessingAgreement from './DataProcessingAgreement';
import AcceptableUsePolicy from './AcceptableUsePolicy';
import ServiceLevelAgreement from './ServiceLevelAgreement';
import CUIPolicy from './CUIPolicy';
import JoinAgency from './JoinAgency';
import AssessmentChat from './AssessmentChat';
import { getControlExplanation } from './data/explanations/index';
import OnboardingChecklist from './components/OnboardingChecklist';
import OnboardingFlow from './components/OnboardingFlow';
import EmptyState from './components/EmptyState';
import ErrorBoundary from './components/ErrorBoundary';
import Toast from './components/Toast';
import SPRSScoreHistory from './components/SPRSScoreHistory';
import TeamManagement from './components/TeamManagement';
import TrialBanner from './TrialBanner';
import TrialGate, { isTrialExpired, TRIAL_LIMITS } from './TrialGate';
import AdminDashboard from './AdminDashboard';
import CompanySettings from './CompanySettings';
import { generateSSPSpreadsheet } from './generateSpreadsheet';
import { AnimatePresence, motion } from 'framer-motion';

// ── SVG Logo Mark ─────────────────────────────────────────────────────────────
function LogoMark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="10" fill="url(#appLogoGrad)" />
      <path
        d="M20 6L8 11v10c0 7.18 5.13 13.89 12 15.5C27.87 34.89 33 28.18 33 21V11L20 6z"
        fill="rgba(255,255,255,0.15)"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="1"
      />
      <path d="M14 20.5l4 4 8-8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id="appLogoGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2D7DD2" /><stop offset="1" stopColor="#1A4E8A" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function statusColor(pct) {
  return pct >= 80 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
}
// eslint-disable-next-line no-unused-vars
function statusLabel(pct) {
  return pct >= 80 ? 'On Track' : pct >= 50 ? 'In Progress' : 'At Risk';
}
function priorityColor(p) {
  return p === 'high' ? 'var(--red)' : p === 'med' ? 'var(--amber)' : 'var(--accent)';
}
function priorityBg(p) {
  return p === 'high' ? 'var(--red-bg)' : p === 'med' ? 'var(--amber-bg)' : 'var(--blue-bg)';
}

// Normalizes plan strings to canonical keys: 'professional', 'business', or null.
// Agency tier is hidden until re-launch — no user has this plan
const isAgency = false;
// Team Management hidden until customer validation
// eslint-disable-next-line no-unused-vars
const isTeamManagement = false;

function normalizePlan(plan) {
  if (!plan) return null; // no plan = not subscribed
  const p = plan.toLowerCase().trim();
  if (p === 'professional') return 'professional';
  if (p === 'business')     return 'business';
  if (p === 'pro')          return 'professional';
  if (p === 'team')         return 'business';
  if (p === 'starter')      return 'professional';
  if (p === 'agency')       return 'business';
  return null; // unknown = unsubscribed
}

// Returns framework-aware nav items. Purely additive — no existing ids removed.
function getNav(framework, userPlan = 'professional') {
  const isCmmc       = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3'].includes(framework);
  const isCmmcOrNist = isCmmc || framework === 'nist_171';
  const fw           = FRAMEWORKS.find(f => f.id === framework) || FRAMEWORKS[1];

  const reportLabel =
    isCmmc                     ? 'Reports & SSP'                      :
    framework === 'soc2'       ? 'Reports & Audit Report'             :
    framework === 'iso27001'   ? 'Reports & Statement of Applicability':
    framework === 'hipaa'      ? 'Reports & Risk Analysis'            :
                                 'Reports & Documentation';

  const sspReaderLabel =
    isCmmcOrNist               ? 'SSP Reader'   :
    framework === 'soc2'       ? 'Policy Reader' :
                                 'Document Reader';

  return [
    // ── OVERVIEW ──────────────────────────────────────────────────────────
    { id: 'dashboard',  label: 'Dashboard',      section: 'Overview',   sectionStyle: 'standard' },

    // ── FRAMEWORKS ────────────────────────────────────────────────────────
    { id: 'frameworks', label: 'All Frameworks', section: 'Frameworks', sectionStyle: 'standard' },

    // ── CURRENT FRAMEWORK ─────────────────────────────────────────────────
    {
      id: 'assessment', label: 'Assessment', section: fw.name,
      sectionStyle: 'framework', sectionColor: fw.color, sectionShortName: fw.shortName,
    },
    { id: 'gaps',      label: 'Gap Analysis'      },
    { id: 'tasks',     label: 'Remediation Tasks' },
    { id: 'evidence',  label: 'Evidence Locker'   },
    { id: 'report',    label: reportLabel },
    { id: 'sspReader', label: sspReaderLabel, requiredPlan: 'professional' },

    // ── TOOLS ─────────────────────────────────────────────────────────────
    { id: 'policies', label: 'Policy Templates', section: 'Tools', sectionStyle: 'tools', toolsItem: true, requiredPlan: 'professional' },
    { id: 'auditor',  label: 'Auditor Access',   toolsItem: true, requiredPlan: 'professional' },
    ...(isCmmc ? [{ id: 'affirmation', label: 'Annual Affirmation', toolsItem: true, requiredPlan: 'professional' }] : []),

    // ── AGENCY (hidden until re-launch) ──────────────────────────────────
    // isAgency is hardcoded false — no agency nav items shown to any user

    // ── ACCOUNT ───────────────────────────────────────────────────────────
    // { id: 'team', label: 'My Team', section: 'Account', sectionStyle: 'standard' }, // hidden until customer validation
    { id: 'companySettings', label: 'Company Settings',  teamItem: true },
    { id: '__pricing__',     label: 'Plans & Pricing',   actionKey: 'upgrade' },
  ];
}

function Sidebar({ view, setView, onLogout, profile, session, framework, onUpgrade, isAdminUser, company, companyMembers, isOpen, onClose, isCompanyOwner, companyRole, agencyTab, onSetAgencyTab, companyOwnerPlan, effectivePlan }) {
  const userPlan = normalizePlan(
    effectivePlan ||
    ((companyRole && companyRole !== 'owner' && companyOwnerPlan)
      ? companyOwnerPlan
      : profile?.plan)
  );
  const nav      = getNav(framework, userPlan);

  // Hide items the user's plan can't access (admin moved to topbar)
  // team / companySettings visibility depends on role
  const visibleNav = nav.filter(item => {
    if (item.requiredPlan && !hasAccess(userPlan, item.requiredPlan)) return false;
    // Hide My Team and Company Settings from auditors
    if (item.id === 'team') {
      if (companyRole === 'auditor') return false;
      return true;
    }
    // Company Settings: only visible to owner/admin
    if (item.id === 'companySettings') {
      if (!companyRole || companyRole === 'auditor' || companyRole === 'member') return false;
      return companyRole === 'owner' || companyRole === 'admin';
    }
    return true;
  });

  const PLAN_BADGE = {
    professional: { label: 'Professional', color: 'var(--accent)', bg: 'var(--blue-bg)',  border: 'rgba(45,125,210,0.2)' },
    business:     { label: 'Business',     color: 'var(--green)',  bg: 'var(--green-bg)', border: 'rgba(29,184,122,0.2)' },
  };
  const badge = PLAN_BADGE[userPlan] || { label: 'Free Trial', color: 'var(--amber)', bg: 'var(--amber-bg)', border: 'rgba(245,166,35,0.2)' };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            display: 'none',
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 199,
          }}
          className="sidebar-backdrop"
        />
      )}
    <div style={{
      width: 256, background: 'var(--bg2)', borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', height: '100vh', flexShrink: 0,
      // Mobile: fixed overlay, hidden by default
      position: 'var(--sidebar-position, relative)',
      zIndex: 200,
      transform: 'var(--sidebar-transform, none)',
      transition: 'transform 0.25s ease',
    }}
    className={isOpen ? 'sidebar sidebar-open' : 'sidebar'}
    >
      {/* Branding */}
      <div className="glass-nav" style={{ padding: '22px 20px 18px', borderRadius: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {profile?.white_label_logo_url ? (
            <img
              src={profile.white_label_logo_url}
              alt="Logo"
              style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover' }}
            />
          ) : profile?.white_label_enabled && profile?.white_label_color ? (
            <div style={{
              width: 34, height: 34, borderRadius: 8, fontSize: 13, fontWeight: 700,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: profile.white_label_color,
            }}>
              {(profile.white_label_name || 'A').charAt(0).toUpperCase()}
            </div>
          ) : (
            <LogoMark size={34} />
          )}
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {profile?.white_label_enabled && profile?.white_label_name
                ? profile.white_label_name
                : 'TheClearAudit'}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
              compliance platform
            </div>
          </div>
        </div>
      </div>

      {/* Profile card */}
      <div
        onClick={() => setView('account')}
        style={{
          margin: '12px 12px 4px', padding: '10px 12px',
          borderRadius: 9, cursor: 'pointer',
          background: view === 'account' ? 'var(--accent-glow)' : 'var(--surface)',
          border: view === 'account' ? '1px solid rgba(45,125,210,0.2)' : '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10,
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (view !== 'account') e.currentTarget.style.background = 'var(--surface2)'; }}
        onMouseLeave={e => { if (view !== 'account') e.currentTarget.style.background = view === 'account' ? 'var(--accent-glow)' : 'var(--surface)'; }}
      >
        {/* Avatar with online dot */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'linear-gradient(135deg, #2D7DD2, #0D3F73)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: '#fff',
            border: '2px solid var(--bg2)',
          }}>
            {(profile?.company_name || session?.user?.email || 'U').charAt(0).toUpperCase()}
          </div>
          {/* Online indicator */}
          <div style={{
            position: 'absolute', bottom: 0, right: 0,
            width: 9, height: 9, borderRadius: '50%',
            background: 'var(--green)',
            border: '2px solid var(--bg2)',
          }} />
        </div>

        {/* Name + plan */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}>
            {profile?.company_name || session?.user?.email || 'My Account'}
          </div>
          <div style={{ marginTop: 3 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
              color: badge.color, background: badge.bg,
              padding: '1px 6px', borderRadius: 8,
              border: `1px solid ${badge.border}`,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {badge.label}
            </span>
            {/* Admin badge hidden until needed — re-enable when customer base grows
            {isAdminUser && (
              <span style={{
                marginLeft: 4, fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
                color: '#a78bfa', background: 'rgba(124,58,237,0.15)',
                padding: '1px 6px', borderRadius: 8,
                border: '1px solid rgba(124,58,237,0.3)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>Admin</span>
            )}
            */}
          </div>
        </div>

        {/* Chevron */}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </div>

      {/* Nav list */}
      <div style={{ padding: '4px 12px', flex: 1, overflowY: 'auto' }}>
        {visibleNav.map(item => (
          <React.Fragment key={item.id}>
            {item.section && (
              item.sectionStyle === 'framework' ? (
                // Colored framework section header
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '14px 8px 6px',
                }}>
                  <div style={{
                    height: 16, paddingInline: 5, borderRadius: 4, flexShrink: 0,
                    background: `${item.sectionColor}22`,
                    border: `1px solid ${item.sectionColor}55`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{
                      fontSize: 8, fontWeight: 800, color: item.sectionColor,
                      fontFamily: 'var(--mono)', letterSpacing: '0.02em', whiteSpace: 'nowrap',
                    }}>
                      {item.sectionShortName}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: item.sectionColor,
                    textTransform: 'uppercase', letterSpacing: '0.07em',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.section}
                  </span>
                </div>
              ) : item.sectionStyle === 'tools' ? (
                // Visually prominent Tools section header
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '16px 8px 6px',
                }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    background: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, color: '#fff', fontWeight: 700,
                  }}>
                    <svg width="8" height="10" viewBox="0 0 10 13" fill="currentColor"><path d="M6 0L0 7h5l-1 6 6-8H5L6 0z"/></svg>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: 'var(--accent)',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}>
                    {item.section}
                  </span>
                </div>
              ) : item.sectionStyle === 'admin' ? (
                // Admin section header (purple)
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '16px 8px 6px',
                }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    background: '#7c3aed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, color: '#fff', fontWeight: 700,
                  }}>
                    <svg width="8" height="9" viewBox="0 0 24 27" fill="currentColor"><path d="M12 0L0 4.5v9C0 20.25 5.4 26.55 12 28.5 18.6 26.55 24 20.25 24 13.5v-9L12 0z"/></svg>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#a78bfa',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}>
                    {item.section}
                  </span>
                </div>
              ) : (
                // Standard muted gray section header
                <div style={{
                  fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase',
                  letterSpacing: '0.1em', padding: '12px 8px 5px', fontWeight: 500,
                }}>
                  {item.section}
                </div>
              )
            )}
            {(() => {
              const memberLimit = userPlan === 'professional' ? 3 : null;
              const memberTotal = (companyMembers?.length || 0) + (company ? 1 : 0);
              const showBadge   = item.id === 'companySettings' && company;
              const badgeText   = memberLimit !== null ? `${memberTotal}/${memberLimit}` : `${memberTotal}`;
              const isPricing   = item.actionKey === 'upgrade';
              const isAgencyItem = !!item.agencyTabTarget;
              const isActiveAgency = isAgencyItem && view === 'agency' && agencyTab === item.agencyTabTarget;
              const isActive    = isPricing ? false : isAgencyItem ? isActiveAgency : view === item.id;
              const handleClick = isPricing ? onUpgrade : isAgencyItem
                ? () => { setView('agency'); if (onSetAgencyTab) onSetAgencyTab(item.agencyTabTarget); }
                : () => setView(item.id);
              return (
                <div onClick={handleClick} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  paddingLeft: isAgencyItem ? 20 : 10,
                  borderRadius: 7, cursor: 'pointer', fontSize: 13, marginBottom: 2,
                  color: isPricing
                    ? 'var(--accent)'
                    : isActive ? 'var(--accent)' : item.toolsItem ? 'var(--text)' : 'var(--text2)',
                  background: isPricing
                    ? 'rgba(45,125,210,0.06)'
                    : isActive
                    ? 'var(--accent-glow)'
                    : item.toolsItem ? 'rgba(45,125,210,0.04)' : 'transparent',
                  border: isPricing
                    ? '1px solid rgba(45,125,210,0.15)'
                    : isActive
                    ? '1px solid rgba(45,125,210,0.2)'
                    : item.toolsItem ? '1px solid rgba(45,125,210,0.07)' : '1px solid transparent',
                  fontWeight: isPricing ? 500 : isActive ? 500 : 400,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (isPricing) { e.currentTarget.style.background = 'rgba(45,125,210,0.12)'; e.currentTarget.style.borderColor = 'rgba(45,125,210,0.3)'; } }}
                onMouseLeave={e => { if (isPricing) { e.currentTarget.style.background = 'rgba(45,125,210,0.06)'; e.currentTarget.style.borderColor = 'rgba(45,125,210,0.15)'; } }}
                >
                  <span style={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                  }}>
                    {item.id === 'companySettings'
                      ? (isCompanyOwner || companyRole === 'admin' ? 'Team Settings' : 'My Team')
                      : item.label}
                  </span>
                  {isPricing && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <polyline points="18 15 12 9 6 15"/>
                    </svg>
                  )}
                  {showBadge && !isPricing && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
                      color: 'var(--text3)', background: 'var(--surface2)',
                      padding: '1px 5px', borderRadius: 6,
                      border: '1px solid var(--border)', flexShrink: 0,
                    }}>
                      {badgeText}
                    </span>
                  )}
                </div>
              );
            })()}
          </React.Fragment>
        ))}

      </div>

      {/* Upgrade banner — pinned above user footer, shown when not on max plan */}
      {!['business'].includes(userPlan) && (
        <div style={{ padding: '8px 12px 0' }}>
          <div
            onClick={onUpgrade}
            style={{
              padding: '9px 12px', borderRadius: 8,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, rgba(45,125,210,0.12), rgba(45,125,210,0.06))',
              border: '1px solid rgba(45,125,210,0.25)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(45,125,210,0.2), rgba(45,125,210,0.1))';
              e.currentTarget.style.borderColor = 'rgba(45,125,210,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(45,125,210,0.12), rgba(45,125,210,0.06))';
              e.currentTarget.style.borderColor = 'rgba(45,125,210,0.25)';
            }}
          >
            <div style={{
              width: 22, height: 22, borderRadius: 5, flexShrink: 0,
              background: 'var(--accent)', display: 'flex', alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 15 12 9 6 15"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', lineHeight: 1.3 }}>
                Upgrade Plan
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.3 }}>
                Unlock more frameworks &amp; tools
              </div>
            </div>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </div>
      )}

      {/* User footer */}
      <div style={{ padding: '10px 14px 16px', borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0, fontSize: 11,
            fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: 'linear-gradient(135deg, #2D7DD2, #0D3F73)',
          }}>
            {(profile?.company_name || 'A').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {company?.name || profile?.company_name || 'My Company'}
            </div>
            <div style={{
              fontSize: 10, color: 'var(--text3)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {session?.user?.email || ''}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            <span style={{
              fontSize: 9, fontFamily: 'var(--mono)',
              color: badge.color, background: badge.bg,
              padding: '2px 7px', borderRadius: 10,
              border: `1px solid ${badge.border}`,
            }}>{badge.label.toUpperCase()}</span>
            {companyRole && (
              <span style={{
                fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
                color: companyRole === 'owner' ? 'var(--accent)' : companyRole === 'admin' ? '#a78bfa' : 'var(--text3)',
                background: companyRole === 'owner' ? 'var(--blue-bg)' : companyRole === 'admin' ? 'rgba(167,139,250,0.1)' : 'var(--surface2)',
                padding: '2px 6px', borderRadius: 8,
                border: `1px solid ${companyRole === 'owner' ? 'rgba(45,125,210,0.25)' : companyRole === 'admin' ? 'rgba(167,139,250,0.3)' : 'var(--border)'}`,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>{companyRole}</span>
            )}
            {companyRole && companyRole !== 'owner' && companyOwnerPlan && (
              <span style={{ fontSize: 8, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                via {company?.name || 'team'}
              </span>
            )}
            {/* Admin badge hidden until needed — re-enable when customer base grows
            {isAdminUser && (
              <span style={{
                fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
                color: '#a78bfa', background: 'rgba(124,58,237,0.15)',
                padding: '2px 6px', borderRadius: 8,
                border: '1px solid rgba(124,58,237,0.3)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>Admin</span>
            )}
            */}
          </div>
        </div>
        {/* Admin nav button hidden until needed — re-enable when customer base grows
        {isAdminUser && (
          <button
            onClick={() => setView('admin')}
            style={{
              width: '100%', padding: '7px 12px', borderRadius: 6, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'var(--font)', textAlign: 'center',
              background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)',
              color: '#a78bfa', transition: 'all 0.15s', marginBottom: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(124,58,237,0.2)';
              e.currentTarget.style.borderColor = 'rgba(124,58,237,0.5)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(124,58,237,0.1)';
              e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
            </svg>
            Admin
          </button>
        )}
        */}
        <button
          onClick={onLogout}
          style={{
            width: '100%', padding: '7px 12px', borderRadius: 6, cursor: 'pointer',
            fontSize: 12, fontWeight: 500, fontFamily: 'var(--font)', textAlign: 'center',
            background: 'transparent', border: '1px solid var(--border2)',
            color: 'var(--text3)', transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--red-bg)';
            e.currentTarget.style.color = 'var(--red)';
            e.currentTarget.style.borderColor = 'var(--red)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text3)';
            e.currentTarget.style.borderColor = 'var(--border2)';
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
    </>
  );
}

function Topbar({ view, level, framework, onAddEvidence, onUpgrade, onExportSSP, onExportSSPDocx, docxLoading, onExportPOAM, onExportSpreadsheet, setView, onMenuToggle, userPlan, notifications, showNotifications, onToggleNotifications, onMarkNotificationRead, onMarkAllRead }) {
  const fw     = FRAMEWORKS.find(f => f.id === framework) || FRAMEWORKS[1];
  const isCmmc = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3'].includes(framework);

  const reportTitle =
    isCmmc                     ? 'Reports & SSP'                       :
    framework === 'soc2'       ? 'Reports & Audit Report'              :
    framework === 'iso27001'   ? 'Reports & Statement of Applicability' :
    framework === 'hipaa'      ? 'Reports & Risk Analysis'             :
                                 'Reports & Documentation';

  const titles = {
    dashboard:       'Compliance Dashboard',
    assessment:      `${fw.name} Assessment`,
    gaps:            `${fw.name} Gap Analysis`,
    tasks:           'Remediation Tasks',
    evidence:        'Evidence Locker',
    report:          reportTitle,
    profile:         'Company Profile',
    billing:         'Billing and Subscription',
    auditor:         'Auditor Access',
    affirmation:     'Annual Affirmation Tracker',
    policies:        'Policy Template Library',
    agency:          'Agency Dashboard',
    sspReader:       'SSP Document Reader',
    frameworks:      'Compliance Frameworks',
    admin:           'Admin Dashboard',
    account:         'Account Settings',
    companySettings: 'Company Settings',
  };

  const subtitles = {
    dashboard:       'Your compliance readiness at a glance',
    assessment:      isCmmc ? `${(LEVELS[level] || LEVELS[2]).chip} · ${(LEVELS[level] || LEVELS[2]).domains.length} Domains` : `${fw.name} · ${fw.controlCount} controls`,
    gaps:            'Controls requiring attention and remediation',
    tasks:           'Track and resolve open compliance gaps',
    evidence:        'Upload and map policies to controls',
    report:          isCmmc ? `${(LEVELS[level] || LEVELS[2]).chip} · ${fw.name}` : `${fw.name}`,
    profile:         'Your company information and CAGE code',
    billing:         'Manage your subscription and payment method',
    auditor:         'Grant read-only access to your C3PAO or auditor',
    affirmation:     'Annual cybersecurity maturity affirmation',
    policies:        'Download editable policy and procedure templates',
    agency:          'Manage client accounts and white-label settings',
    sspReader:       'Import SSP data into your assessment',
    frameworks:      'Switch between compliance frameworks',
    admin:           'Platform user and subscription management',
    account:         'Profile, subscription, security, and notifications',
    companySettings: 'Team members, roles, and framework access',
  };
  return (
    <div className="app-topbar" style={{
      background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
      padding: '0 20px 0 16px', height: 58, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Hamburger — mobile only */}
        <button
          onClick={onMenuToggle}
          className="hamburger-btn"
          aria-label="Toggle menu"
          style={{
            display: 'none', // shown via CSS on mobile
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 6, borderRadius: 6, color: 'var(--text2)', flexShrink: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{titles[view] || 'Dashboard'}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>
            {subtitles[view] || ''}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 11, padding: '4px 10px', borderRadius: 20, fontWeight: 500,
          background: 'var(--blue-bg)', color: 'var(--accent)',
          border: '1px solid rgba(45,125,210,0.2)',
        }}>{isCmmc ? (LEVELS[level] || LEVELS[2]).chip : fw.shortName}</span>
        {/* Notification Bell */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={onToggleNotifications}
            style={{
              position: 'relative', width: 34, height: 34, borderRadius: 7,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: showNotifications ? 'var(--surface2)' : 'var(--surface)',
              color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {notifications.filter(n => !n.is_read).length > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4,
                background: 'var(--red)', color: '#fff',
                fontSize: 9, fontWeight: 700, minWidth: 16, height: 16,
                borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 4px',
              }}>
                {notifications.filter(n => !n.is_read).length > 99 ? '99+' : notifications.filter(n => !n.is_read).length}
              </span>
            )}
          </button>
          {showNotifications && (
            <div className="notification-panel" style={{
              position: 'absolute', top: 42, right: 0, width: 360,
              maxHeight: 400, background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              overflow: 'hidden', display: 'flex', flexDirection: 'column', zIndex: 999,
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Notifications</span>
                {notifications.some(n => !n.is_read) && (
                  <button onClick={onMarkAllRead} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)' }}>
                    Mark all read
                  </button>
                )}
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {notifications.length === 0 && (
                  <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>No notifications</div>
                )}
                {notifications.map(n => (
                  <div
                    key={n.id}
                    onClick={() => { onMarkNotificationRead(n.id); if (n.link_view) setView(n.link_view); }}
                    style={{
                      padding: '12px 16px', borderBottom: '1px solid var(--border)',
                      cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
                      background: n.is_read ? 'transparent' : 'rgba(45,125,210,0.05)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                    onMouseLeave={e => e.currentTarget.style.background = n.is_read ? 'transparent' : 'rgba(45,125,210,0.05)'}
                  >
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                      background: n.is_read ? 'transparent' : 'var(--accent)',
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: n.is_read ? 400 : 600, color: 'var(--text)', marginBottom: 2 }}>{n.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.body}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                        {(() => {
                          const mins = Math.floor((Date.now() - new Date(n.created_at)) / 60000);
                          if (mins < 1) return 'Just now';
                          if (mins < 60) return `${mins}m ago`;
                          const hrs = Math.floor(mins / 60);
                          if (hrs < 24) return `${hrs}h ago`;
                          return `${Math.floor(hrs / 24)}d ago`;
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="topbar-export-btns" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={onExportSSP}
            style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--surface2)';
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--surface)';
              e.currentTarget.style.color = 'var(--text2)';
            }}
          >
            Export SSP
          </button>
          <button
            onClick={onExportSSPDocx}
            disabled={docxLoading}
            style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
              cursor: docxLoading ? 'not-allowed' : 'pointer',
              border: '1px solid var(--accent)', background: 'transparent',
              color: 'var(--accent)', fontFamily: 'var(--font)',
              transition: 'all 0.15s', opacity: docxLoading ? 0.6 : 1,
            }}
            onMouseEnter={e => { if (!docxLoading) { e.currentTarget.style.background = 'var(--blue-bg)'; } }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            {docxLoading ? 'Generating…' : 'Export Word'}
          </button>
          <button
            onClick={onExportPOAM}
            style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--surface2)';
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--surface)';
              e.currentTarget.style.color = 'var(--text2)';
            }}
          >
            Export POA&M
          </button>
          <button
            onClick={onExportSpreadsheet}
            style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--surface2)';
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--surface)';
              e.currentTarget.style.color = 'var(--text2)';
            }}
          >
            SSP Spreadsheet
          </button>
          <button onClick={onAddEvidence} style={{
            fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
          }}>+ Add Evidence</button>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ level, answers, framework, evidenceCount, company, companyMembers, onViewCompanySettings, onStartAssessment, onGoToEvidence, onGoToReport, onGoToTasks, tasks, evidence, affirmations, narratives, session, setView }) {
  const lv      = LEVELS[level];
  const isCmmc  = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3'].includes(framework);

  // Real stats from actual answers
  const questions   = getQuestionsByLevel(level, framework);
  const realMet     = questions.filter(q => answers[q.id] === 'yes').length;
  const realPartial = questions.filter(q => answers[q.id] === 'partial').length;
  const realGaps    = questions.filter(q => answers[q.id] === 'no').length;
  const realTotal   = questions.length;
  const realScore   = realTotal > 0
    ? Math.round(((realMet + realPartial * 0.5) / realTotal) * 100) : 0;

  // Domain breakdown from actual answers
  const domainMap = {};
  questions.forEach(q => {
    if (!domainMap[q.domain]) {
      const lvDomain = lv.domains?.find(d => d.id === q.domain);
      domainMap[q.domain] = { id: q.domain, name: lvDomain?.name || q.domain, met: 0, total: 0 };
    }
    domainMap[q.domain].total++;
    if (answers[q.id] === 'yes') domainMap[q.domain].met++;
  });
  const computedDomains = Object.values(domainMap)
    .map(d => ({ ...d, pct: d.total > 0 ? Math.round((d.met / d.total) * 100) : 0 }))
    .sort((a, b) => a.id.localeCompare(b.id));

  // Framework-specific scores (used in hero display)
  const soc2Score    = framework === 'soc2'     ? calculateSOC2Score(answers)     : null;
  const isoScore     = framework === 'iso27001'  ? calculateISO27001Score(answers) : null;
  const hipaaScore   = framework === 'hipaa'     ? calculateHIPAAScore(answers)    : null;
  const csfScore     = framework === 'nist_csf'  ? calculateNISTCSFScore(answers)  : null;
  const pciScore     = framework === 'pci_dss'   ? calculatePCIDSSScore(answers)   : null;
  const fedrampScore = framework === 'fedramp'   ? calculateFedRAMPScore(answers)  : null;

  // Generic percentage for other non-CMMC frameworks
  const genericAnswered = Object.values(answers).filter(v => v === 'yes' || v === 'partial' || v === 'no').length;
  const genericYes      = Object.values(answers).filter(v => v === 'yes').length;
  // eslint-disable-next-line no-unused-vars
  const genericPartial  = Object.values(answers).filter(v => v === 'partial').length;
  const genericScore    = genericAnswered > 0
    ? Math.round(((genericYes + genericPartial * 0.5) / genericAnswered) * 100) : 0;

  // Check if user has started any assessment
  const hasAnyAnswers = Object.keys(answers).length > 0 &&
    Object.values(answers).some(v => v === 'yes' || v === 'partial' || v === 'no');

  // ── Activity feed state ─────────────────────────────────────────────────────
  const [activityFeed, setActivityFeed]     = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [domainsAnimated, setDomainsAnimated] = useState(false);

  const userId = session?.user?.id;

  useEffect(() => {
    let cancelled = false;
    async function fetchActivity() {
      if (!userId) { setActivityLoading(false); return; }
      try {
        const data = await getFullControlHistory(userId);
        if (!cancelled) setActivityFeed((data || []).slice(0, 10));
      } catch (_) {}
      if (!cancelled) setActivityLoading(false);
    }
    fetchActivity();
    return () => { cancelled = true; };
  }, [userId]);

  // Animate domain bars on mount
  useEffect(() => {
    const t = setTimeout(() => setDomainsAnimated(true), 120);
    return () => clearTimeout(t);
  }, []);

  // ── Deadline calc ───────────────────────────────────────────────────────────
  const isDeadlineFramework = isCmmc || framework === 'nist_171';
  const deadlineDate = new Date('2026-11-01');
  const today = new Date();
  const daysLeft = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));

  // ── Hero score ──────────────────────────────────────────────────────────────
  const sprsRaw = (isCmmc || framework === 'nist_171') ? calculateSPRSScore(answers) : null;

  // Unified display score for hero card
  const heroDisplay = sprsRaw !== null
    ? (sprsRaw > 0 ? `+${sprsRaw}` : String(sprsRaw))
    : soc2Score !== null    ? `${soc2Score}%`
    : isoScore !== null     ? `${isoScore}%`
    : hipaaScore !== null   ? `${hipaaScore}%`
    : csfScore !== null     ? `${csfScore}%`
    : pciScore !== null     ? `${pciScore}%`
    : fedrampScore !== null ? `${fedrampScore}%`
    : `${genericScore}%`;

  const heroColor = sprsRaw !== null
    ? (sprsRaw >= 88 ? 'var(--green)' : sprsRaw >= 0 ? 'var(--amber)' : 'var(--red)')
    : realScore >= 70 ? 'var(--accent)' : realScore >= 40 ? 'var(--amber)' : 'var(--red)';

  function heroStatusInfo() {
    if (sprsRaw !== null) {
      if (sprsRaw >= 110) return { text: 'Excellent — Audit Ready',                     color: 'var(--green)' };
      if (sprsRaw >= 88)  return { text: 'Good — Meets DoD Minimum',                    color: 'var(--accent)' };
      if (sprsRaw >= 0)   return { text: 'Developing — Significant Work Needed',         color: 'var(--amber)' };
      return              { text: 'At Risk — Major Gaps Identified',                     color: 'var(--red)' };
    }
    if (realScore >= 90) return { text: 'Excellent — Audit Ready',                       color: 'var(--green)' };
    if (realScore >= 70) return { text: 'Good — Minor Gaps Remain',                      color: 'var(--accent)' };
    if (realScore >= 50) return { text: 'Developing — Significant Work Needed',           color: 'var(--amber)' };
    return               { text: 'At Risk — Major Gaps Identified',                      color: 'var(--red)' };
  }
  const statusInfo = heroStatusInfo();

  // Progress ring (percentage of controls met)
  const ringPct          = realScore;
  const ringColor        = ringPct >= 80 ? 'var(--green)' : ringPct >= 50 ? 'var(--amber)' : 'var(--red)';
  const ringRadius       = 52;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset       = ringCircumference - (ringPct / 100) * ringCircumference;

  // ── Action items ────────────────────────────────────────────────────────────
  const openTasks    = (tasks || []).filter(t => !t.done);
  const highTasks    = openTasks.filter(t => (t.priority || '').toLowerCase() === 'high');
  const topTask      = highTasks[0] || openTasks[0] || null;

  const answeredDomains = computedDomains.filter(d => d.total > 0);
  const weakestDomain   = answeredDomains.length > 0
    ? [...answeredDomains].sort((a, b) => a.pct - b.pct)[0] : null;
  const allDomainsStrong = answeredDomains.length > 0 && answeredDomains.every(d => d.pct >= 80);

  const expiredEv   = (evidence || []).filter(ev => ev.expires_at && new Date(ev.expires_at) < today);
  const expiring60  = (evidence || []).filter(ev => {
    if (!ev.expires_at) return false;
    const days = Math.floor((new Date(ev.expires_at) - today) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 60;
  }).sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));

  // ── Evidence health (for CMMC section) ─────────────────────────────────────
  const expiredEvCount   = expiredEv.length;
  const expiringEvCount  = (evidence || []).filter(ev => {
    if (!ev.expires_at) return false;
    const days = Math.floor((new Date(ev.expires_at) - today) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 30;
  }).length;

  // ── Narratives (for CMMC section) ──────────────────────────────────────────
  const narrativeMap = {};
  (narratives || []).forEach(n => { narrativeMap[n.control_id] = n.narrative || ''; });
  const documentedNarratives = questions.filter(q => (narrativeMap[q.id] || '').length >= 50).length;
  const narrativePct = questions.length > 0 ? Math.round((documentedNarratives / questions.length) * 100) : 0;

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function timeAgo(ds) {
    const date = new Date(ds);
    const secs = Math.floor((today - date) / 1000);
    if (secs < 60)  return 'Just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60)  return `${mins}m ago`;
    const hrs  = Math.floor(mins / 60);
    if (hrs  < 24)  return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7)   return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function fmtShortDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return (
    <div className="view-pad" style={{ padding: 28, overflowY: 'auto', height: '100%' }}>
      <style>{`
        @keyframes dbShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
        .db-skeleton {
          background: linear-gradient(90deg, var(--bg2) 25%, var(--surface) 50%, var(--bg2) 75%);
          background-size: 200% 100%;
          animation: dbShimmer 1.5s infinite;
          border-radius: 6px;
        }
        @media (max-width: 768px) {
          .db-hero-grid    { grid-template-columns: 1fr !important; }
          .db-actions-grid { grid-template-columns: 1fr !important; }
          .db-domain-grid  { grid-template-columns: 1fr !important; }
          .db-bottom-grid  { grid-template-columns: 1fr !important; }
          .db-readiness-grid { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 769px) and (max-width: 1100px) {
          .db-domain-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* ── SECTION 1: Deadline Urgency Banner ──────────────────────────────── */}
      {isDeadlineFramework && (
        <div style={{
          marginBottom: 20, padding: '14px 20px', borderRadius: 12,
          border: `1px solid ${daysLeft > 180 ? 'var(--amber)' : 'var(--red)'}`,
          background: daysLeft > 180 ? 'var(--amber-bg)' : 'var(--red-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '1.5px',
              textTransform: 'uppercase',
              color: daysLeft > 180 ? 'var(--amber)' : 'var(--red)',
              marginBottom: 4,
            }}>
              CMMC PHASE 2 DEADLINE
            </div>
            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, lineHeight: 1.5 }}>
              <strong style={{ color: daysLeft > 180 ? 'var(--amber)' : 'var(--red)' }}>
                {daysLeft} days
              </strong>{' '}
              until November 2026 enforcement — get certified before DoD contracts require CMMC
            </div>
          </div>
          <button
            onClick={() => setView('gaps')}
            style={{
              flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '7px 14px',
              borderRadius: 7, cursor: 'pointer', fontFamily: 'var(--font)',
              border: `1px solid ${daysLeft > 180 ? 'var(--amber)' : 'var(--red)'}`,
              background: 'transparent',
              color: daysLeft > 180 ? 'var(--amber)' : 'var(--red)',
            }}
          >
            View Roadmap →
          </button>
        </div>
      )}

      {/* ── SECTION 2: SPRS Score Hero Card ─────────────────────────────────── */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border2)',
        borderRadius: 16, padding: 32, marginBottom: 20,
      }}>
        {!hasAnyAnswers ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
              <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                <path d="M36 6L12 15v18c0 16.2 11.76 31.32 24 34.56C48.24 64.32 60 49.2 60 33V15L36 6z"
                  fill="var(--blue-bg)" stroke="var(--accent)" strokeWidth="2"/>
                <circle cx="36" cy="34" r="8" fill="none" stroke="var(--text3)" strokeWidth="2"/>
                <line x1="36" y1="24" x2="36" y2="22" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round"/>
                <line x1="36" y1="46" x2="36" y2="44" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round"/>
                <line x1="24" y1="34" x2="22" y2="34" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round"/>
                <line x1="50" y1="34" x2="48" y2="34" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              No assessment data yet
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, maxWidth: 380, margin: '0 auto 20px' }}>
              Complete your {isCmmc ? 'CMMC' : framework === 'nist_171' ? 'NIST SP 800-171' : 'compliance'} assessment to see your score and compliance status.
            </div>
            <button
              onClick={onStartAssessment}
              style={{
                fontSize: 13, fontWeight: 600, padding: '10px 24px', borderRadius: 8,
                cursor: 'pointer', border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
              }}
            >
              Start Assessment →
            </button>
          </div>
        ) : (
          <div className="db-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 200px', gap: 32, alignItems: 'center' }}>
            {/* Left — score */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 10 }}>
                {(isCmmc || framework === 'nist_171') ? 'SPRS SCORE' : 'OVERALL READINESS'}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 72, fontWeight: 800, lineHeight: 1, color: heroColor, fontFamily: 'var(--mono)' }}>
                  {heroDisplay}
                </span>
                {(isCmmc || framework === 'nist_171') && (
                  <span style={{ fontSize: 24, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>/110</span>
                )}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 12px', borderRadius: 20,
                background: `${statusInfo.color}18`,
                border: `1px solid ${statusInfo.color}40`,
                marginBottom: 12,
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: statusInfo.color }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: statusInfo.color }}>{statusInfo.text}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                Last updated today · {questions.length} controls evaluated
              </div>
            </div>

            {/* Middle — quick stats */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                {
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ),
                  label: 'Controls Implemented',
                  value: `${realMet} of ${realTotal}`,
                  color: 'var(--green)',
                },
                {
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={realGaps > 20 ? 'var(--red)' : realGaps > 10 ? 'var(--amber)' : 'var(--green)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  ),
                  label: 'Open Gaps',
                  value: `${realGaps} controls`,
                  color: realGaps > 20 ? 'var(--red)' : realGaps > 10 ? 'var(--amber)' : 'var(--green)',
                },
                {
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  ),
                  label: 'Evidence Files',
                  value: `${evidenceCount || 0} uploaded`,
                  color: 'var(--text)',
                },
              ].map(row => (
                <div key={row.label} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', background: 'var(--surface)',
                  borderRadius: 8, border: '1px solid var(--border)',
                }}>
                  <div style={{ flexShrink: 0 }}>{row.icon}</div>
                  <div style={{ flex: 1, fontSize: 11, color: 'var(--text3)' }}>{row.label}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: row.color, fontFamily: 'var(--mono)', flexShrink: 0 }}>{row.value}</div>
                </div>
              ))}
            </div>

            {/* Right — SVG progress ring */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <svg width="140" height="140" viewBox="0 0 140 140" style={{ overflow: 'visible' }}>
                <circle cx="70" cy="70" r={ringRadius} fill="none" stroke="var(--border2)" strokeWidth="10"/>
                <circle
                  cx="70" cy="70" r={ringRadius} fill="none"
                  stroke={ringColor} strokeWidth="10"
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringOffset}
                  strokeLinecap="round"
                  transform="rotate(-90 70 70)"
                  style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
                />
                <text x="70" y="65" textAnchor="middle" fill="var(--text)" fontSize="22" fontWeight="700" fontFamily="var(--font)">{ringPct}%</text>
                <text x="70" y="83" textAnchor="middle" fill="var(--text3)" fontSize="10" fontFamily="var(--font)">complete</text>
              </svg>
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>Overall Compliance</div>
            </div>
          </div>
        )}
      </div>

      {/* ── SECTION 3: Action Items Row ──────────────────────────────────────── */}
      <div className="db-actions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>

        {/* Action 1 — Tasks */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 12, padding: 20, position: 'relative' }}>
          {topTask ? (
            <>
              <div style={{ position: 'absolute', top: 14, right: 14 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  background: topTask.priority === 'high' ? 'var(--red-bg)' : 'var(--amber-bg)',
                  color: topTask.priority === 'high' ? 'var(--red)' : 'var(--amber)',
                }}>
                  {topTask.priority === 'high' ? 'HIGH' : 'MED'}
                </span>
              </div>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: topTask.priority === 'high' ? 'var(--red-bg)' : 'var(--amber-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={topTask.priority === 'high' ? 'var(--red)' : 'var(--amber)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4, paddingRight: 44, lineHeight: 1.4 }}>
                {(topTask.name || 'Untitled task').slice(0, 48)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14, lineHeight: 1.4 }}>
                {topTask.due ? `Due ${topTask.due}` : 'No due date'}{topTask.domain ? ` · ${topTask.domain}` : ''}
              </div>
              <button onClick={onGoToTasks} style={{ fontSize: 11, fontWeight: 600, padding: '6px 13px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font)' }}>
                View Task →
              </button>
            </>
          ) : (
            <>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--green-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>No open tasks</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>All remediation tasks are complete — great work!</div>
              <button onClick={onGoToTasks} style={{ fontSize: 11, fontWeight: 600, padding: '6px 13px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font)' }}>
                Manage Tasks →
              </button>
            </>
          )}
        </div>

        {/* Action 2 — Weakest domain */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 12, padding: 20, position: 'relative' }}>
          {weakestDomain && !allDomainsStrong ? (
            <>
              <div style={{ position: 'absolute', top: 14, right: 14 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  background: weakestDomain.pct < 50 ? 'var(--red-bg)' : 'var(--amber-bg)',
                  color: weakestDomain.pct < 50 ? 'var(--red)' : 'var(--amber)',
                }}>
                  {weakestDomain.pct < 50 ? 'HIGH' : 'MED'}
                </span>
              </div>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: weakestDomain.pct < 50 ? 'var(--red-bg)' : 'var(--amber-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={weakestDomain.pct < 50 ? 'var(--red)' : 'var(--amber)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4, paddingRight: 44, lineHeight: 1.4 }}>
                Weakest Domain: {weakestDomain.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>
                {weakestDomain.met} of {weakestDomain.total} controls implemented ({weakestDomain.pct}%)
              </div>
              <button onClick={() => setView('gaps')} style={{ fontSize: 11, fontWeight: 600, padding: '6px 13px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font)' }}>
                Fix Gaps →
              </button>
            </>
          ) : (
            <>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--green-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Strong compliance posture</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>
                {answeredDomains.length > 0 ? 'All domains are above 80% — excellent progress!' : 'Complete your assessment to see domain gaps.'}
              </div>
              <button onClick={() => setView('assessment')} style={{ fontSize: 11, fontWeight: 600, padding: '6px 13px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font)' }}>
                View Assessment →
              </button>
            </>
          )}
        </div>

        {/* Action 3 — Evidence */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 12, padding: 20, position: 'relative' }}>
          {(expiredEv.length > 0 || expiring60.length > 0) ? (
            <>
              <div style={{ position: 'absolute', top: 14, right: 14 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  background: expiredEv.length > 0 ? 'var(--red-bg)' : 'var(--amber-bg)',
                  color: expiredEv.length > 0 ? 'var(--red)' : 'var(--amber)',
                }}>
                  {expiredEv.length > 0 ? 'HIGH' : 'MED'}
                </span>
              </div>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: expiredEv.length > 0 ? 'var(--red-bg)' : 'var(--amber-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={expiredEv.length > 0 ? 'var(--red)' : 'var(--amber)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.34a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4, paddingRight: 44, lineHeight: 1.4 }}>
                {expiredEv.length > 0
                  ? `${expiredEv.length} Evidence File${expiredEv.length > 1 ? 's' : ''} Expired`
                  : `${expiring60.length} Evidence File${expiring60.length > 1 ? 's' : ''} Expiring Soon`}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14, lineHeight: 1.4 }}>
                {expiring60[0]
                  ? `Earliest: ${(expiring60[0].title || expiring60[0].name || 'file').slice(0, 28)} · ${fmtShortDate(expiring60[0].expires_at)}`
                  : 'Review and renew expired evidence files'}
              </div>
              <button onClick={onGoToEvidence} style={{ fontSize: 11, fontWeight: 600, padding: '6px 13px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font)' }}>
                Review Evidence →
              </button>
            </>
          ) : (
            <>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--green-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>All evidence is current</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>No files expiring within 60 days — stay proactive!</div>
              <button onClick={onGoToEvidence} style={{ fontSize: 11, fontWeight: 600, padding: '6px 13px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font)' }}>
                Go to Evidence →
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── SECTION 4: Domain Readiness Grid ─────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Domain Readiness</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>
              Completion status across all {computedDomains.length} domains
            </div>
          </div>
          <button
            onClick={onStartAssessment}
            style={{ fontSize: 12, fontWeight: 500, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font)' }}
          >
            View Full Assessment →
          </button>
        </div>

        {computedDomains.length === 0 ? (
          <div className="db-domain-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 10, padding: 16 }}>
                <div className="db-skeleton" style={{ height: 11, width: '65%', marginBottom: 10 }} />
                <div className="db-skeleton" style={{ height: 6, width: '100%', marginBottom: 8 }} />
                <div className="db-skeleton" style={{ height: 10, width: '35%' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="db-domain-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {computedDomains.map(d => {
              const barColor = d.pct >= 80 ? 'var(--green)' : d.pct >= 50 ? 'var(--amber)' : 'var(--red)';
              return (
                <button
                  key={d.id}
                  onClick={onStartAssessment}
                  style={{
                    background: 'var(--bg2)', border: '1px solid var(--border2)',
                    borderRadius: 10, padding: '16px 16px 16px 20px',
                    position: 'relative', overflow: 'hidden',
                    textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font)',
                    transition: 'border-color 0.15s, transform 0.15s',
                    width: '100%',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = barColor; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.transform = 'none'; }}
                >
                  {/* Left color bar */}
                  <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: barColor }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4, maxWidth: '75%' }}>{d.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: barColor, fontFamily: 'var(--mono)', flexShrink: 0 }}>{d.pct}%</div>
                  </div>
                  <div style={{ height: 6, background: 'var(--border2)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{
                      height: '100%',
                      width: domainsAnimated ? `${d.pct}%` : '0%',
                      background: barColor, borderRadius: 3,
                      transition: 'width 0.8s ease-out',
                    }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{d.met}/{d.total} controls</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── SECTION 5: Recent Activity Feed ─────────────────────────────────── */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 12, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Recent Activity</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Last 10 actions in your compliance program</div>
          </div>
          <button onClick={onStartAssessment} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', padding: 0 }}>
            View All →
          </button>
        </div>
        {activityLoading ? (
          <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="db-skeleton" style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="db-skeleton" style={{ height: 11, width: '68%', marginBottom: 6 }} />
                  <div className="db-skeleton" style={{ height: 9, width: '28%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : activityFeed.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 99, background: 'var(--surface)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text3)', margin: '0 auto 12px',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>No activity yet</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.55, maxWidth: 280, margin: '0 auto 14px' }}>
              Start your assessment to begin tracking compliance progress and activity.
            </div>
            <button
              onClick={onStartAssessment}
              style={{ fontSize: 11, fontWeight: 600, padding: '7px 16px', borderRadius: 7, cursor: 'pointer', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)' }}
            >
              Start Assessment
            </button>
          </div>
        ) : (
          <div style={{ padding: '4px 0' }}>
            {activityFeed.map((entry, idx) => {
              const isLast = idx === activityFeed.length - 1;
              const ns = (entry.new_status || '').toLowerCase();
              const isYes      = ns === 'yes';
              const isPartial  = ns === 'partial';
              const isNo       = ns === 'no';
              const iconColor  = isYes ? 'var(--green)' : isPartial ? 'var(--amber)' : isNo ? 'var(--red)' : 'var(--accent)';
              const icon = isYes || isPartial ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              ) : isNo ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              );
              const prevStatus = entry.old_status ? ` from ${entry.old_status}` : '';
              const description = entry.control_id
                ? `${entry.control_id} marked as ${entry.new_status || 'updated'}${prevStatus}`
                : 'Control status updated';
              return (
                <div key={entry.id || idx} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '10px 20px',
                  borderBottom: isLast ? 'none' : '1px solid var(--border)',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                    background: `${iconColor}18`, border: `1px solid ${iconColor}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: iconColor,
                  }}>
                    {icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.4 }}>{description}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{timeAgo(entry.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── SECTION 6: Onboarding Checklist + SPRS History ───────────────────── */}
      <div className="db-bottom-grid" style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 20, marginBottom: 20, alignItems: 'start' }}>
        <div>
          <OnboardingChecklist session={session} setView={setView} />
        </div>
        {(isCmmc || framework === 'nist_171') && (
          <div>
            <SPRSScoreHistory session={session} currentScore={calculateSPRSScore(answers)} />
          </div>
        )}
      </div>

      {/* ── CMMC/NIST: Readiness gauge + Evidence health + Narratives ────────── */}
      {(isCmmc || framework === 'nist_171') && (() => {
        const controlsList = questions.map(q => ({ id: q.id, answer: answers[q.id] }));
        const readinessResult = calculateReadinessScore(controlsList, evidence || [], tasks || [], affirmations || [], narratives || []);
        return (
          <div className="db-readiness-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>CMMC Readiness</div>
              <ReadinessGauge result={readinessResult} size={120} />
            </div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>Evidence Health</div>
              {[
                { label: 'Total Evidence', val: (evidence || []).length,                                    color: 'var(--text)'   },
                { label: 'Expired',        val: expiredEvCount,    color: expiredEvCount  > 0 ? 'var(--red)'   : 'var(--text3)' },
                { label: 'Expiring 30d',   val: expiringEvCount,   color: expiringEvCount > 0 ? 'var(--amber)' : 'var(--text3)' },
                { label: 'Healthy',        val: (evidence || []).length - expiredEvCount - expiringEvCount, color: 'var(--green)'  },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text3)' }}>{r.label}</span>
                  <span style={{ fontWeight: 600, color: r.color }}>{r.val}</span>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>SSP Narratives</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: narrativePct >= 75 ? 'var(--green)' : narrativePct >= 50 ? 'var(--amber)' : 'var(--red)', marginBottom: 4 }}>
                {documentedNarratives}
                <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text3)' }}> / {questions.length}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>controls with documented implementation</div>
              <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                <div style={{ height: '100%', width: `${narrativePct}%`, background: narrativePct >= 75 ? 'var(--green)' : narrativePct >= 50 ? 'var(--amber)' : 'var(--red)', borderRadius: 3, transition: 'width 0.5s ease' }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{narrativePct}% documented · 100% required for audit-ready SSP</div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

// ── Assessment Intro ──────────────────────────────────────────────────────────
function AssessmentIntro({ answers, framework, level, onStart, onReset }) {
  const fw        = FRAMEWORKS.find(f => f.id === framework) || FRAMEWORKS[1];
  const questions = getQuestionsByLevel(level, framework);
  const answered  = questions.filter(q => answers[q.id] === 'yes' || answers[q.id] === 'partial' || answers[q.id] === 'no').length;
  const hasStarted = answered > 0;
  const pct       = questions.length > 0 ? Math.round((answered / questions.length) * 100) : 0;
  const estMins   = Math.max(1, Math.round(questions.length / 10));

  const isCmmc = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3'].includes(framework);

  const FRAMEWORK_BULLETS = {
    cmmc_l1:  [
      'Covers 17 foundational cybersecurity practices across 6 domains',
      'Assesses protection of Federal Contract Information (FCI)',
      'Required for all DoD contractors handling FCI',
      'After completion, download your SSP and generate a gap analysis',
    ],
    cmmc_l2:  [
      'Covers all 110 NIST SP 800-171 controls across 14 domains',
      'Assesses protection of Controlled Unclassified Information (CUI)',
      'Aligns with CMMC 2.0 Level 2 Third-Party Assessment requirements',
      'Your SPRS score is automatically calculated from your responses',
    ],
    cmmc_l3:  [
      'Covers 130+ advanced practices based on NIST SP 800-172',
      'Designed for organizations supporting critical defense programs',
      'Addresses advanced persistent threats (APTs)',
      'Requires a government-led assessment for official certification',
    ],
    nist_171: [
      'Covers all 110 NIST SP 800-171 requirements for protecting CUI',
      'Independent of CMMC — used for self-assessment and compliance tracking',
      'Generates an SPRS-equivalent score upon completion',
      'Results feed directly into your SSP and POA&M documents',
    ],
    soc2:     [
      'Covers 64 controls across the AICPA Trust Services Criteria',
      'Evaluates security, availability, confidentiality, and privacy',
      'Used to demonstrate compliance to enterprise customers',
      'Generates a readiness report for your external audit preparation',
    ],
    iso27001: [
      'Covers 93 Annex A controls from the ISO/IEC 27001:2022 standard',
      'Assesses your Information Security Management System (ISMS)',
      'Calculates a conformance score from your control responses',
      'Generates a Statement of Applicability (SoA) report',
    ],
    hipaa:    [
      'Covers 69 safeguards from the HIPAA Security Rule',
      'Evaluates administrative, physical, and technical controls',
      'Required for covered entities and business associates handling PHI',
      'Generates a HIPAA Security Risk Analysis report',
    ],
    nist_csf: [
      'Covers 106 subcategories across 6 NIST CSF 2.0 Functions',
      'Functions: Govern, Identify, Protect, Detect, Respond, Recover',
      'Provides a maturity score and current-state profile',
      'Useful for any organization regardless of industry or size',
    ],
    pci_dss:  [
      'Covers 285 controls across 12 PCI DSS v4.0 Requirements',
      'Assesses security of cardholder data environments (CDE)',
      'Required for any organization that stores, processes, or transmits card data',
      'Generates a PCI DSS Self-Assessment Report',
    ],
    fedramp:  [
      'Covers 325 controls based on NIST SP 800-53 Rev 5',
      'FedRAMP Moderate baseline for cloud products used by federal agencies',
      'Addresses security, privacy, and supply chain risk management',
      'Generates a FedRAMP Security Assessment Report',
    ],
  };
  const bullets = FRAMEWORK_BULLETS[framework] || [
    `Covers ${questions.length} controls across multiple domains`,
    'Answer each control with Implemented, Partial, or Not Implemented',
    'Your responses are saved automatically as you go',
    'View your results in the Gap Analysis and Reports sections',
  ];

  return (
    <div className="view-pad" style={{
      padding: 28, overflowY: 'auto', height: '100%',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    }}>
      <div style={{ width: '100%', maxWidth: 680 }}>
        {/* Framework header */}
        <div style={{
          background: `${fw.color}10`, border: `1px solid ${fw.color}40`,
          borderRadius: 14, padding: '28px 32px', marginBottom: 20,
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Top strip */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: fw.color,
          }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 13, flexShrink: 0,
              background: `${fw.color}20`, border: `2px solid ${fw.color}60`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: fw.color, fontFamily: 'var(--mono)', letterSpacing: '-0.03em' }}>
                {fw.shortName.split(' ')[0]}
              </span>
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, marginBottom: 4 }}>
                {fw.name} Assessment
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>{fw.description}</div>
            </div>
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Controls',     value: questions.length,    color: fw.color      },
              { label: 'Est. Time',    value: `~${estMins} min`,   color: 'var(--text2)' },
              { label: 'Domains',      value: isCmmc ? (LEVELS[level]?.domains?.length || '—') : '—',   color: 'var(--text2)' },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, minWidth: 100, padding: '10px 14px', borderRadius: 8,
                background: 'var(--bg2)', border: '1px solid var(--border)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.color, lineHeight: 1, marginBottom: 4 }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress (if started) */}
        {hasStarted && (
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '16px 20px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
                  Assessment In Progress
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {answered} of {questions.length} controls answered ({pct}% complete)
                </div>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8,
                color: fw.color, background: `${fw.color}15`, border: `1px solid ${fw.color}40`,
              }}>
                {pct}%
              </span>
            </div>
            <div style={{ height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct}%`, background: fw.color,
                borderRadius: 4, transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        )}

        {/* What this covers */}
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '20px 22px', marginBottom: 24,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
            What this assessment covers
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {bullets.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                  background: `${fw.color}18`, border: `1px solid ${fw.color}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: 1,
                }}>
                  <span style={{ fontSize: 9, color: fw.color, fontWeight: 700 }}>✓</span>
                </div>
                <span style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.55 }}>{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA buttons */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={onStart}
            style={{
              flex: 1, minWidth: 200, padding: '14px 24px', borderRadius: 9,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              border: `2px solid ${fw.color}`,
              background: fw.color, color: '#fff', fontFamily: 'var(--font)',
              transition: 'all 0.15s', boxShadow: `0 4px 16px ${fw.color}30`,
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {hasStarted ? 'Continue Assessment →' : 'Start Assessment →'}
          </button>

          {hasStarted && (
            <button
              onClick={onReset}
              style={{
                padding: '14px 20px', borderRadius: 9, fontSize: 13, fontWeight: 500,
                cursor: 'pointer', border: '1px solid var(--border2)',
                background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--red)';
                e.currentTarget.style.color = 'var(--red)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border2)';
                e.currentTarget.style.color = 'var(--text3)';
              }}
            >
              Reset Assessment
            </button>
          )}
        </div>

        {/* Note */}
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 14, textAlign: 'center', lineHeight: 1.6 }}>
          Your answers are saved automatically as you go. You can pause and return at any time.
        </div>
      </div>
    </div>
  );
}

function Assessment({ answers, setAnswer, level, framework, onBack, session, userPlan, onUpgrade, getNarrative, handleNarrativeChange, narratives, evidence, controlFilters, setControlFilters }) {
  const [currentControl, setCurrentControl] = useState(null);
  const [openExplanation, setOpenExplanation] = useState(null); // control id
  const [openNarrativeControl, setOpenNarrativeControl] = useState(null);
  const [sortOrder, setSortOrder] = useState('default');
  const allQuestions = getQuestionsByLevel(level, framework);

  // Build evidence control id set
  const evidenceControlIds = new Set((evidence || []).flatMap(ev => ev.control_ids || []));
  // Build narrative map
  const narrativeMap = {};
  (narratives || []).forEach(n => { narrativeMap[n.control_id] = n.narrative || ''; });

  // Client-side filtering
  const filters = controlFilters || { statuses: [], domain: '', hasEvidence: false, hasNarrative: false };
  let questions = allQuestions.filter(q => {
    if (filters.statuses.length > 0 && !filters.statuses.includes(answers[q.id] || 'unanswered')) return false;
    if (filters.domain && q.domain !== filters.domain) return false;
    if (filters.hasEvidence && !evidenceControlIds.has(q.id)) return false;
    if (filters.hasNarrative && (narrativeMap[q.id] || '').length < 50) return false;
    return true;
  });
  if (sortOrder === 'status') questions = [...questions].sort((a, b) => (answers[a.id] || 'z').localeCompare(answers[b.id] || 'z'));
  else if (sortOrder === 'domain') questions = [...questions].sort((a, b) => a.domain.localeCompare(b.domain));
  else if (sortOrder === 'has_evidence') questions = [...questions].sort((a, b) => (evidenceControlIds.has(b.id) ? 1 : -1));

  const activeFilterCount = filters.statuses.length + (filters.domain ? 1 : 0) + (filters.hasEvidence ? 1 : 0) + (filters.hasNarrative ? 1 : 0);

  const answered  = Object.keys(answers).filter(k => allQuestions.find(q => q.id === k)).length;
  const pct       = Math.round((answered / allQuestions.length) * 100);

  const isCmmc    = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3'].includes(framework);
  const fw        = FRAMEWORKS.find(f => f.id === framework) || FRAMEWORKS[1];
  const subtitle  =
    isCmmc                      ? `CMMC Level ${level} - NIST SP 800-171 Controls` :
    framework === 'nist_171'    ? 'NIST SP 800-171 Controls'                        :
    framework === 'soc2'        ? 'SOC 2 Trust Services Criteria'                   :
    framework === 'iso27001'    ? 'ISO 27001:2022 Annex A Controls'                 :
    framework === 'hipaa'       ? 'HIPAA Security Rule Safeguards'                  :
    framework === 'nist_csf'    ? 'NIST CSF 2.0 Subcategories'                      :
                                  `${fw.name} Controls`;

  return (
    <div className="view-pad" style={{ padding: 28, overflowY: 'auto', height: '100%' }}>
      <div className="assessment-header" style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 20,
      }}>
        <div>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none',
                cursor: 'pointer', padding: '0 0 6px 0', display: 'block', fontFamily: 'var(--font)',
              }}
            >← Back to overview</button>
          )}
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
            Self-Assessment Questionnaire
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {subtitle}
          </div>
        </div>
        <div className="assessment-progress-bar-wrap" style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
            {answered} of {allQuestions.length} answered
          </div>
          <div style={{ width: 200, height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: 'var(--accent)', borderRadius: 3, transition: 'width 0.3s',
            }} />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {/* Status pills */}
        {[
          { val: 'yes',         label: 'Implemented',  color: 'var(--green)' },
          { val: 'partial',     label: 'Partial',       color: 'var(--amber)' },
          { val: 'no',          label: 'Not Impl.',     color: 'var(--red)'   },
          { val: 'unanswered',  label: 'Unanswered',    color: 'var(--text3)' },
        ].map(s => {
          const active = filters.statuses.includes(s.val);
          return (
            <button key={s.val} onClick={() => {
              const newStatuses = active ? filters.statuses.filter(x => x !== s.val) : [...filters.statuses, s.val];
              setControlFilters(f => ({ ...f, statuses: newStatuses }));
            }} style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--font)',
              border: `1px solid ${active ? s.color : 'var(--border2)'}`,
              background: active ? `${s.color}18` : 'transparent',
              color: active ? s.color : 'var(--text3)', transition: 'all 0.15s',
            }}>
              {s.label}
            </button>
          );
        })}
        {/* Domain dropdown */}
        <select
          value={filters.domain}
          onChange={e => setControlFilters(f => ({ ...f, domain: e.target.value }))}
          style={{ fontSize: 11, padding: '4px 8px', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--font)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)' }}
        >
          <option value="">All Domains</option>
          {[...new Set(allQuestions.map(q => q.domain))].sort().map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {/* Has Evidence toggle */}
        <button onClick={() => setControlFilters(f => ({ ...f, hasEvidence: !f.hasEvidence }))} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--font)', border: `1px solid ${filters.hasEvidence ? 'var(--amber)' : 'var(--border2)'}`, background: filters.hasEvidence ? 'var(--amber-bg)' : 'transparent', color: filters.hasEvidence ? 'var(--amber)' : 'var(--text3)', transition: 'all 0.15s' }}>Has Evidence</button>
        {/* Has Narrative toggle */}
        <button onClick={() => setControlFilters(f => ({ ...f, hasNarrative: !f.hasNarrative }))} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--font)', border: `1px solid ${filters.hasNarrative ? 'var(--accent)' : 'var(--border2)'}`, background: filters.hasNarrative ? 'var(--blue-bg)' : 'transparent', color: filters.hasNarrative ? 'var(--accent)' : 'var(--text3)', transition: 'all 0.15s' }}>Has Narrative</button>
        {/* Sort */}
        <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--font)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', marginLeft: 'auto' }}>
          <option value="default">Sort: Default</option>
          <option value="status">Sort: Status</option>
          <option value="domain">Sort: Domain</option>
          <option value="has_evidence">Sort: Has Evidence</option>
        </select>
        {activeFilterCount > 0 && (
          <button onClick={() => setControlFilters({ statuses: [], domain: '', hasEvidence: false, hasNarrative: false })} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--font)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--red)', transition: 'all 0.15s' }}>
            Clear ({activeFilterCount})
          </button>
        )}
      </div>
      {questions.length < allQuestions.length && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Showing {questions.length} of {allQuestions.length} controls</div>
      )}

      {questions.map((q, i) => (
        <div key={q.id}
          onClick={() => setCurrentControl({ id: q.id, title: q.text.substring(0, 60), domain: q.domain, description: q.text, status: answers[q.id] || 'unanswered' })}
          style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 10, padding: 20, marginBottom: 10, cursor: 'default',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{
              fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--accent)',
              background: 'var(--blue-bg)', padding: '2px 7px', borderRadius: 4,
            }}>{q.id}</span>
            <span style={{ fontSize: 10, color: 'var(--text3)' }}>
              {q.domain} Domain - Question {i + 1} of {questions.length}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.55, marginBottom: 14 }}>
            {q.text}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['yes', 'partial', 'no'].map(val => {
              const labels = { yes: 'Implemented', partial: 'Partially', no: 'Not Implemented' };
              const active = answers[q.id] === val;
              const activeColors = {
                yes:     { bg: 'var(--green-bg)', border: 'var(--green)', color: 'var(--green)' },
                partial: { bg: 'var(--amber-bg)', border: 'var(--amber)', color: 'var(--amber)' },
                no:      { bg: 'var(--red-bg)',   border: 'var(--red)',   color: 'var(--red)'   },
              };
              const c = activeColors[val];
              return (
                <button key={val} onClick={() => setAnswer(q.id, val)} style={{
                  flex: 1, padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                  fontSize: 11, fontWeight: active ? 500 : 400, fontFamily: 'var(--font)',
                  textAlign: 'center', transition: 'all 0.15s',
                  background: active ? c.bg : 'transparent',
                  border: `1px solid ${active ? c.border : 'var(--border2)'}`,
                  color: active ? c.color : 'var(--text2)',
                }}>
                  {labels[val]}
                </button>
              );
            })}
          </div>
          {/* Narrative panel */}
          {session && getNarrative && (() => {
            const text    = getNarrative(q.id);
            const charLen = text.length;
            const quality = charLen === 0   ? null
                          : charLen < 50    ? { label: 'Brief',              color: 'var(--text3)' }
                          : charLen < 150   ? { label: 'Developing',         color: 'var(--amber)' }
                          :                  { label: 'Detailed — audit ready', color: 'var(--green)' };
            const isOpen = openNarrativeControl === q.id;
            return (
              <div style={{ marginTop: 10 }}>
                <button
                  onClick={e => { e.stopPropagation(); setOpenNarrativeControl(isOpen ? null : q.id); }}
                  style={{ fontSize: 11, color: quality ? quality.color : 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                  {isOpen ? 'Hide narrative' : quality ? `Narrative: ${quality.label}` : 'Add implementation narrative'}
                </button>
                {isOpen && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 5 }}>How does your organization implement this control?</div>
                    <textarea
                      value={text}
                      onChange={e => { e.stopPropagation(); handleNarrativeChange(q.id, e.target.value); }}
                      onClick={e => e.stopPropagation()}
                      maxLength={1000}
                      rows={4}
                      placeholder="Describe your organization's implementation approach, tools used, responsible parties, and any compensating controls..."
                      style={{
                        width: '100%', background: 'var(--bg)', border: '1px solid var(--border)',
                        borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--text)',
                        fontFamily: 'var(--font)', outline: 'none', resize: 'vertical',
                        minHeight: 80, boxSizing: 'border-box', lineHeight: 1.6,
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                      {quality && <span style={{ fontSize: 10, color: quality.color, fontWeight: 500 }}>{quality.label}</span>}
                      <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 'auto' }}>{charLen}/1000</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {/* What does this mean? panel */}
          {(() => {
            const exp = getControlExplanation(framework, q.id);
            const frameworkRefLabel = {
              cmmc_l1: 'NIST SP 800-171 Practice',
              cmmc_l2: 'NIST SP 800-171 Practice',
              cmmc_l3: 'NIST SP 800-171 Practice',
              nist_171: 'NIST SP 800-171 Practice',
              soc2: 'SOC 2 Criterion',
              iso27001: 'ISO 27001:2022 Control',
              hipaa: 'HIPAA Security Rule',
              nist_csf: 'NIST CSF 2.0 Subcategory',
              pci_dss: 'PCI DSS v4.0 Requirement',
              fedramp: 'FedRAMP Moderate Control',
            }[framework] || 'Control Reference';
            return (
              <div style={{ marginTop: 10 }}>
                <button
                  onClick={e => { e.stopPropagation(); setOpenExplanation(openExplanation === q.id ? null : q.id); }}
                  style={{
                    fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none',
                    cursor: 'pointer', padding: '2px 0', fontFamily: 'var(--font)', display: 'flex',
                    alignItems: 'center', gap: 4,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {openExplanation === q.id ? 'Hide explanation' : 'What does this mean?'}
                </button>
                {openExplanation === q.id && (
                  <div style={{
                    marginTop: 8, background: 'var(--bg)', borderRadius: 8, padding: '14px 16px',
                    borderLeft: exp.isPlaceholder ? '3px solid var(--amber)' : '3px solid var(--accent)',
                    border: exp.isPlaceholder ? '1px solid var(--amber)' : '1px solid var(--border)',
                    animation: 'fadeIn 0.2s ease',
                  }}>
                    {exp.isPlaceholder ? (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Reference</div>
                        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                          This control is defined in NIST SP 800-171 Rev 2. Refer to the official publication at
                          {' '}<span style={{ color: 'var(--accent)', fontFamily: 'var(--mono)' }}>csrc.nist.gov/publications/detail/sp/800-171/rev-2/final</span>{' '}
                          for the complete requirement text and supplemental guidance.
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Plain English</div>
                          <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>{exp.plain_english}</div>
                        </div>
                        {exp.example && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Example</div>
                            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>{exp.example}</div>
                          </div>
                        )}
                        {exp.evidence_tips && exp.evidence_tips.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Evidence Tips</div>
                            {exp.evidence_tips.map((tip, ti) => (
                              <div key={ti} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: 'var(--text2)', lineHeight: 1.55, marginBottom: 3 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                {tip}
                              </div>
                            ))}
                          </div>
                        )}
                        {exp.common_mistakes && (
                          <div style={{
                            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
                            borderRadius: 6, padding: '8px 10px', fontSize: 11, color: 'var(--text2)', lineHeight: 1.55,
                          }}>
                            <span style={{ fontWeight: 600, color: 'var(--amber)' }}>Common mistake: </span>{exp.common_mistakes}
                          </div>
                        )}
                        <div style={{ marginTop: 6, fontSize: 10, color: 'var(--text3)' }}>{frameworkRefLabel} {exp.nist_reference}</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      ))}
      <AssessmentChat
        currentControl={currentControl}
        session={session}
        userPlan={userPlan}
        onUpgrade={onUpgrade}
      />
    </div>
  );
}

const DOMAIN_NAMES = {
  AC: 'Access Control',      AT: 'Awareness & Training', AU: 'Audit & Accountability',
  CM: 'Config Management',   IA: 'Identification & Auth', IR: 'Incident Response',
  MA: 'Maintenance',         MP: 'Media Protection',      PS: 'Personnel Security',
  PE: 'Physical Protection', RA: 'Risk Assessment',       CA: 'Security Assessment',
  SC: 'System & Comms',      SI: 'System & Info Integrity',
};

function calcGapPriority(controlId, domainPct) {
  const w = Math.abs(CONTROL_WEIGHTS[controlId] || 1);
  if (w >= 3 && domainPct < 50)  return 'high';
  if (w >= 3 || domainPct < 75)  return 'medium';
  return 'low';
}

function GapAnalysis({ answers, questions, onExportPOAM, tasks, evidence, addTask, setView, framework, profile, userPlan, isActiveTrial }) {
  const [searchText,    setSearchText]    = useState('');
  const [domainFilter,  setDomainFilter]  = useState('');
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [priorityFilter,setPriorityFilter] = useState('all');
  const [sortOrder,     setSortOrder]     = useState('sprs');
  const [activeDomain,  setActiveDomain]  = useState('');
  const [expandedCards, setExpandedCards] = useState({});

  // ── Domain statistics ───────────────────────────────────────────
  const domainStats = useMemo(() => {
    const map = {};
    questions.forEach(q => {
      if (!map[q.domain]) map[q.domain] = { total: 0, yes: 0, partial: 0, no: 0, unanswered: 0, sprsAtRisk: 0 };
      map[q.domain].total++;
      const a = answers[q.id];
      if (a === 'yes')         map[q.domain].yes++;
      else if (a === 'partial') map[q.domain].partial++;
      else if (a === 'no')    { map[q.domain].no++; map[q.domain].sprsAtRisk += Math.abs(CONTROL_WEIGHTS[q.id] || 1); }
      else                      map[q.domain].unanswered++;
    });
    return Object.entries(map).map(([domain, s]) => ({
      domain, ...s,
      pct: s.total > 0 ? Math.round((s.yes / s.total) * 100) : 0,
    })).sort((a, b) => a.pct - b.pct);
  }, [questions, answers]);

  // ── All gaps with enriched data ──────────────────────────────────
  const allGaps = useMemo(() => {
    return questions
      .filter(q => answers[q.id] === 'no' || answers[q.id] === 'partial')
      .map(q => {
        const ds       = domainStats.find(d => d.domain === q.domain);
        const domainPct = ds ? ds.pct : 0;
        const weight    = Math.abs(CONTROL_WEIGHTS[q.id] || 1);
        const priority  = calcGapPriority(q.id, domainPct);
        const exp       = getControlExplanation(framework || 'cmmc_l2', q.id);
        const taskExists = (tasks || []).some(t =>
          (t.name || '').includes(q.id) || t.control_id === q.id
        );
        const evForControl = (evidence || []).filter(ev =>
          (ev.control_ids || []).includes(q.id) || ev.control_id === q.id
        );
        return {
          id: q.id, domain: q.domain, requirement: q.text,
          status:      answers[q.id] === 'no' ? 'Not Implemented' : 'Partial',
          weight, priority, domainPct,
          plainEnglish: exp.isPlaceholder ? null : exp.plain_english,
          taskExists, evidenceCount: evForControl.length,
        };
      });
  }, [questions, answers, domainStats, tasks, evidence, framework]);

  // ── Filtered + sorted gap list ───────────────────────────────────
  const filteredGaps = useMemo(() => {
    let result = [...allGaps];
    const effectiveDomain = activeDomain || domainFilter;
    if (effectiveDomain)  result = result.filter(g => g.domain === effectiveDomain);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(g => g.id.toLowerCase().includes(q) || g.requirement.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      const match = statusFilter === 'no' ? 'Not Implemented' : 'Partial';
      result = result.filter(g => g.status === match);
    }
    if (priorityFilter !== 'all') result = result.filter(g => g.priority === priorityFilter);
    if (sortOrder === 'sprs')   result.sort((a, b) => b.weight - a.weight);
    else if (sortOrder === 'domain') result.sort((a, b) => a.domain.localeCompare(b.domain));
    else if (sortOrder === 'id')     result.sort((a, b) => a.id.localeCompare(b.id));
    return result;
  }, [allGaps, activeDomain, domainFilter, searchText, statusFilter, priorityFilter, sortOrder]);

  // ── Summary stats ────────────────────────────────────────────────
  const totalGaps      = allGaps.length;
  const sprsAtRisk     = questions.filter(q => answers[q.id] === 'no').reduce((s, q) => s + (CONTROL_WEIGHTS[q.id] || 0), 0);
  const highPrioCount  = allGaps.filter(g => g.priority === 'high').length;
  const estWeeks       = Math.max(1, Math.ceil(totalGaps / 5));
  const implemented    = questions.filter(q => answers[q.id] === 'yes').length;
  const partialCount   = questions.filter(q => answers[q.id] === 'partial').length;
  const notImplCount   = questions.filter(q => answers[q.id] === 'no').length;
  const notAnswered    = questions.length - implemented - partialCount - notImplCount;
  const total          = questions.length;

  const gapColor = totalGaps > 20 ? 'var(--red)' : totalGaps > 10 ? 'var(--amber)' : 'var(--green)';

  const hasFilters = searchText || domainFilter || activeDomain || statusFilter !== 'all' || priorityFilter !== 'all' || sortOrder !== 'sprs';

  // ── Add task action ──────────────────────────────────────────────
  async function handleAddTask(gap) {
    if (!addTask) return;
    await addTask({
      name:     `[${gap.id}] ${gap.requirement.slice(0, 60)}`,
      domain:   gap.domain,
      priority: gap.priority === 'high' ? 'high' : gap.priority === 'medium' ? 'med' : 'low',
      owner:    '',
      due:      '',
    });
    if (setView) setView('tasks');
  }

  // ── PDF Export ───────────────────────────────────────────────────
  function handleExportSummary() {
    if (isActiveTrial) {
      window.dispatchEvent(new CustomEvent('theclearaudit-toast', { detail: { message: 'Upgrade to export the Gap Summary PDF', type: 'warning' } }));
      return;
    }
    const companyName = profile?.company_name || 'Your Organization';
    const dateStr     = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const fw          = framework ? framework.toUpperCase().replace('_', ' ') : 'CMMC L2';
    const doc         = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW       = doc.internal.pageSize.getWidth();

    // Cover header
    doc.setFillColor(13, 27, 42);
    doc.rect(0, 0, pageW, 36, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Gap Analysis Summary', 14, 15);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${companyName}  ·  ${fw}  ·  ${dateStr}`, 14, 24);

    const isCmmc = ['cmmc_l1','cmmc_l2','cmmc_l3','nist_171'].includes(framework);
    if (isCmmc) {
      doc.text(`SPRS Score: ${calculateSPRSScore(answers)} / 110`, pageW - 14, 24, { align: 'right' });
    }

    // Section 1: Summary Stats
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('1. Executive Summary', 14, 48);
    autoTable(doc, {
      startY: 52,
      head: [['Metric', 'Value']],
      body: [
        ['Total Gaps',           totalGaps],
        ['SPRS Points at Risk',  sprsAtRisk],
        ['High Priority Gaps',   highPrioCount],
        ['Est. Weeks to Remediate', estWeeks],
        ['Controls Implemented', `${implemented} of ${total}`],
        ['Controls Partially Implemented', partialCount],
      ],
      theme: 'grid',
      headStyles: { fillColor: [13, 27, 42], textColor: 255, fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 }, 1: { cellWidth: 30 } },
      margin: { left: 14, right: 14 },
    });

    // Section 2: Domain Heat Map
    const afterStats = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('2. Domain Risk Heat Map', 14, afterStats);
    autoTable(doc, {
      startY: afterStats + 4,
      head: [['Domain', 'Completion %', 'Implemented', 'Total', 'Status', 'SPRS at Risk']],
      body: domainStats.map(d => [
        DOMAIN_NAMES[d.domain] || d.domain,
        `${d.pct}%`,
        d.yes,
        d.total,
        d.pct >= 80 ? 'On Track' : d.pct >= 50 ? 'In Progress' : 'At Risk',
        d.sprsAtRisk > 0 ? `-${d.sprsAtRisk} pts` : '—',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [13, 27, 42], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });

    // Section 3: Top 10 Priority Gaps
    const afterDomains = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('3. Top Priority Gaps', 14, afterDomains);
    const top10 = [...allGaps]
      .sort((a, b) => {
        const po = { high: 0, medium: 1, low: 2 };
        return (po[a.priority] - po[b.priority]) || (b.weight - a.weight);
      })
      .slice(0, 10);
    autoTable(doc, {
      startY: afterDomains + 4,
      head: [['Control ID', 'Domain', 'Priority', 'SPRS Impact', 'Status']],
      body: top10.map(g => [
        g.id, g.domain, g.priority.toUpperCase(),
        `-${g.weight} pts`, g.status,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [13, 27, 42], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });

    // Section 4: Recommended Next Steps
    const afterGaps = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('4. Recommended Next Steps', 14, afterGaps);
    const worstDomains = domainStats.slice(0, 2);
    const totalHighPrioSprs = allGaps.filter(g => g.priority === 'high').reduce((s, g) => s + g.weight, 0);
    const recommendation = worstDomains.length >= 2
      ? `Focus remediation efforts on ${DOMAIN_NAMES[worstDomains[0].domain] || worstDomains[0].domain} (${worstDomains[0].pct}% complete) and ${DOMAIN_NAMES[worstDomains[1].domain] || worstDomains[1].domain} (${worstDomains[1].pct}% complete) first. Addressing these ${highPrioCount} high-priority controls will improve your SPRS score by up to ${totalHighPrioSprs} points.`
      : `Address the ${highPrioCount} high-priority gaps to improve your SPRS score by up to ${totalHighPrioSprs} points before your C3PAO assessment.`;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(recommendation, pageW - 28);
    doc.text(lines, 14, afterGaps + 8);

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated by TheClearAudit — ${dateStr}`, 14, doc.internal.pageSize.getHeight() - 8);

    const safeName = companyName.replace(/[^a-zA-Z0-9]/g, '_');
    const safeDate = new Date().toISOString().slice(0, 10);
    doc.save(`TheClearAudit_Gap_Summary_${safeName}_${safeDate}.pdf`);
  }

  // ── Empty states ─────────────────────────────────────────────────
  const noAssessment = Object.keys(answers).length === 0;

  return (
    <div className="view-pad" style={{ padding: 28, overflowY: 'auto', height: '100%' }}>
      {/* ── Page header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>Gap Analysis</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {allGaps.length} finding{allGaps.length !== 1 ? 's' : ''} requiring remediation
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={handleExportSummary}
            style={{
              fontSize: 12, fontWeight: 600, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
            }}
          >Export Summary</button>
          <button
            onClick={onExportPOAM}
            style={{
              fontSize: 12, fontWeight: 600, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--accent)',
              background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
            }}
          >Export POA&M</button>
        </div>
      </div>

      {noAssessment ? (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <EmptyState
            icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>}
            title="No assessment started"
            description="Answer controls in the Assessment view to see your gaps and findings here."
            ctaLabel="Start Assessment"
            onCta={() => setView && setView('assessment')}
          />
        </div>
      ) : (
        <>
          {/* ── Summary stat boxes ── */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16,
          }} className="gap-stats-grid">
            {[
              { label: 'Total Gaps',           value: totalGaps,       color: gapColor,          suffix: '' },
              { label: 'SPRS Points at Risk',  value: sprsAtRisk,      color: 'var(--red)',       suffix: '' },
              { label: 'High Priority Gaps',   value: highPrioCount,   color: 'var(--red)',       suffix: '' },
              { label: 'Est. Weeks to Remediate', value: estWeeks,     color: 'var(--amber)',     suffix: 'w' },
            ].map(stat => (
              <div key={stat.label} style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '16px 20px',
              }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: stat.color, lineHeight: 1.1, marginBottom: 4 }}>
                  {stat.value}{stat.suffix}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* ── Progress bar ── */}
          {total > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', gap: 1 }}>
                {implemented > 0  && <div style={{ flex: implemented,  background: 'var(--green)' }} />}
                {partialCount > 0 && <div style={{ flex: partialCount, background: 'var(--amber)' }} />}
                {notImplCount > 0 && <div style={{ flex: notImplCount, background: 'var(--red)'   }} />}
                {notAnswered > 0  && <div style={{ flex: notAnswered,  background: 'var(--surface2)' }} />}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                {[
                  { count: implemented,  color: 'var(--green)',    label: 'Implemented' },
                  { count: partialCount, color: 'var(--amber)',    label: 'Partial' },
                  { count: notImplCount, color: 'var(--red)',      label: 'Not Implemented' },
                  { count: notAnswered,  color: 'var(--text3)',    label: 'Not Answered' },
                ].map(s => (
                  <span key={s.label} style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />
                    {s.count} {s.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Domain Heat Map ── */}
          {domainStats.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>
                Domain Risk Heat Map
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
              }} className="domain-heatmap-grid">
                {domainStats.map(d => {
                  const isActive = activeDomain === d.domain;
                  const bg    = d.pct >= 80 ? 'var(--green-bg)' : d.pct >= 50 ? 'var(--amber-bg)' : 'var(--red-bg)';
                  const clr   = d.pct >= 80 ? 'var(--green)'    : d.pct >= 50 ? 'var(--amber)'    : 'var(--red)';
                  const bdr   = isActive
                    ? clr
                    : d.pct >= 80 ? 'rgba(29,184,122,0.35)' : d.pct >= 50 ? 'rgba(245,166,35,0.35)' : 'rgba(224,82,82,0.35)';
                  return (
                    <div
                      key={d.domain}
                      onClick={() => setActiveDomain(activeDomain === d.domain ? '' : d.domain)}
                      style={{
                        background: bg, border: `1px solid ${bdr}`,
                        borderRadius: 10, padding: '12px 14px',
                        cursor: 'pointer', transition: 'all 0.15s',
                        boxShadow: isActive ? `0 0 0 2px ${clr}` : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: clr }}>
                          {d.domain} — {DOMAIN_NAMES[d.domain] || d.domain}
                        </div>
                        {d.sprsAtRisk > 0 && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10,
                            background: 'var(--red-bg)', color: 'var(--red)',
                            border: '1px solid rgba(224,82,82,0.3)', flexShrink: 0, marginLeft: 4,
                          }}>-{d.sprsAtRisk} pts</span>
                        )}
                      </div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: clr, lineHeight: 1, marginBottom: 6 }}>
                        {d.pct}%
                      </div>
                      <div style={{ height: 4, background: 'rgba(0,0,0,0.1)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
                        <div style={{ height: '100%', width: `${d.pct}%`, background: clr, borderRadius: 2 }} />
                      </div>
                      <div style={{ fontSize: 10, color: clr, opacity: 0.8 }}>
                        {d.yes} of {d.total} implemented
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Filter row ── */}
          <div style={{
            display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <input
              type="text"
              placeholder="Search controls…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{
                flex: '1 1 180px', minWidth: 140, background: 'var(--surface)',
                border: '1px solid var(--border2)', borderRadius: 7,
                padding: '7px 12px', fontSize: 12, color: 'var(--text)',
                fontFamily: 'var(--font)', outline: 'none',
              }}
            />
            <select
              value={activeDomain || domainFilter}
              onChange={e => { setDomainFilter(e.target.value); setActiveDomain(''); }}
              style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 7, padding: '7px 10px', fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font)', cursor: 'pointer' }}
            >
              <option value="">All Domains</option>
              {[...new Set(questions.map(q => q.domain))].sort().map(d => (
                <option key={d} value={d}>{d} — {DOMAIN_NAMES[d] || d}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 7, padding: '7px 10px', fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font)', cursor: 'pointer' }}
            >
              <option value="all">All Status</option>
              <option value="no">Not Implemented</option>
              <option value="partial">Partial</option>
            </select>
            <select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 7, padding: '7px 10px', fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font)', cursor: 'pointer' }}
            >
              <option value="all">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 7, padding: '7px 10px', fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font)', cursor: 'pointer' }}
            >
              <option value="sprs">SPRS Impact (High → Low)</option>
              <option value="domain">Domain</option>
              <option value="id">Control ID</option>
            </select>
            {hasFilters && (
              <button
                onClick={() => { setSearchText(''); setDomainFilter(''); setStatusFilter('all'); setPriorityFilter('all'); setSortOrder('sprs'); setActiveDomain(''); }}
                style={{
                  fontSize: 11, padding: '7px 12px', borderRadius: 7, cursor: 'pointer',
                  border: '1px solid var(--border2)', background: 'var(--surface)',
                  color: 'var(--text3)', fontFamily: 'var(--font)',
                }}
              >Clear filters</button>
            )}
          </div>

          {/* ── Gap count after filter ── */}
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            Showing {filteredGaps.length} of {allGaps.length} gaps
            {(activeDomain || domainFilter) ? ` in ${activeDomain || domainFilter}` : ''}
          </div>

          {/* ── Gap cards ── */}
          {filteredGaps.length === 0 ? (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <EmptyState
                icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>}
                title="No gaps match your filters"
                description="Try adjusting your search or filters to find controls."
                accent="var(--green)"
              />
            </div>
          ) : allGaps.length === 0 ? (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <EmptyState
                icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>}
                title="No gaps found"
                description="All answered controls are implemented. Keep it up."
                accent="var(--green)"
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filteredGaps.map(g => {
                const isExpanded = expandedCards[g.id];
                const pColor = g.priority === 'high' ? 'var(--red)' : g.priority === 'medium' ? 'var(--amber)' : 'var(--accent)';
                const pBg    = g.priority === 'high' ? 'var(--red-bg)' : g.priority === 'medium' ? 'var(--amber-bg)' : 'var(--blue-bg)';
                return (
                  <div key={g.id} style={{
                    background: 'var(--bg2)', border: '1px solid var(--border2)',
                    borderRadius: 10, padding: 16,
                  }}>
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                        {g.id}
                      </span>
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 20,
                        background: 'var(--surface)', border: '1px solid var(--border2)', color: 'var(--text3)',
                      }}>{g.domain}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: pBg, color: pColor, textTransform: 'uppercase',
                      }}>{g.priority}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                        background: 'var(--red-bg)', color: 'var(--red)',
                      }}>-{g.weight} pts</span>
                      <span style={{ marginLeft: 'auto', flexShrink: 0,
                        fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                        background: g.status === 'Partial' ? 'var(--amber-bg)' : 'var(--red-bg)',
                        color:      g.status === 'Partial' ? 'var(--amber)'    : 'var(--red)',
                      }}>{g.status}</span>
                    </div>

                    {/* Body */}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                        {!isExpanded && g.requirement.length > 120
                          ? g.requirement.slice(0, 120) + '…'
                          : g.requirement}
                        {g.requirement.length > 120 && (
                          <button
                            onClick={() => setExpandedCards(prev => ({ ...prev, [g.id]: !isExpanded }))}
                            style={{
                              background: 'none', border: 'none', color: 'var(--accent)',
                              fontSize: 11, cursor: 'pointer', marginLeft: 4, padding: 0,
                              fontFamily: 'var(--font)',
                            }}
                          >{isExpanded ? 'Show less' : 'Show more'}</button>
                        )}
                      </div>
                      {g.plainEnglish && (
                        <div style={{
                          fontSize: 12, color: 'var(--text3)', fontStyle: 'italic',
                          lineHeight: 1.5, marginTop: 6, paddingLeft: 10,
                          borderLeft: '2px solid var(--border2)',
                        }}>
                          {g.plainEnglish.length > 140 ? g.plainEnglish.slice(0, 140) + '…' : g.plainEnglish}
                        </div>
                      )}
                    </div>

                    {/* Footer action row */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {g.taskExists ? (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 6,
                          background: 'var(--green-bg)', color: 'var(--green)',
                          border: '1px solid rgba(29,184,122,0.3)',
                        }}>✓ Task exists</span>
                      ) : (
                        <button
                          onClick={() => handleAddTask(g)}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 6,
                            cursor: 'pointer', border: '1px solid var(--border2)',
                            background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
                          }}
                        >+ Add Task</button>
                      )}
                      {g.evidenceCount > 0 ? (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 6,
                          background: 'var(--blue-bg)', color: 'var(--accent)',
                          border: '1px solid rgba(45,125,210,0.25)',
                        }}>📎 Evidence: {g.evidenceCount} file{g.evidenceCount !== 1 ? 's' : ''}</span>
                      ) : (
                        <button
                          onClick={() => setView && setView('evidence')}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 6,
                            cursor: 'pointer', border: '1px solid var(--border2)',
                            background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
                          }}
                        >Upload Evidence</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <style>{`
        @media (max-width: 900px) { .gap-stats-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 600px) { .domain-heatmap-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

const PRIORITY_ORDER = { high: 0, medium: 1, med: 1, low: 2 };
function normP(p) { return p === 'med' ? 'medium' : (p || 'low'); }

// CMMC 180-day POA&M countdown from task creation date
function getDaysRemaining(createdAt) {
  if (!createdAt) return null;
  const due = new Date(createdAt);
  due.setDate(due.getDate() + 180);
  const today = new Date();
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
}

function PoamBadge({ createdAt }) {
  const days = getDaysRemaining(createdAt);
  if (days === null) return null;
  if (days <= 0) {
    const overdue = Math.abs(days);
    return (
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
        background: 'var(--red-bg)', color: 'var(--red)',
        border: '1px solid var(--red)',
        animation: 'ldpulse 1.5s ease-in-out infinite',
        fontFamily: 'var(--mono)',
      }}>
        OVERDUE {overdue}d
      </span>
    );
  }
  if (days <= 30) {
    return (
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
        background: 'var(--red-bg)', color: 'var(--red)',
        border: '1px solid var(--red)', fontFamily: 'var(--mono)',
      }}>
        {days}d — URGENT
      </span>
    );
  }
  if (days <= 60) {
    return (
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
        background: 'var(--amber-bg)', color: 'var(--amber)',
        border: '1px solid var(--amber)', fontFamily: 'var(--mono)',
      }}>
        {days}d left
      </span>
    );
  }
  return (
    <span style={{
      fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
      background: 'var(--green-bg)', color: 'var(--green)',
      border: '1px solid var(--green)', fontFamily: 'var(--mono)',
    }}>
      {days}d left
    </span>
  );
}

function Tasks({ tasks, toggleTask, deleteTask, addTask }) {
  const [showModal,      setShowModal]      = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [urgencyFilter,  setUrgencyFilter]  = useState('all');
  const [sortBy,         setSortBy]         = useState('urgency');
  const [form, setForm] = useState({
    name: '', description: '', domain: 'AC', priority: 'high', owner: '', due: '',
  });

  const filtered = tasks
    .filter(t => {
      if (statusFilter === 'open')      return !t.done;
      if (statusFilter === 'completed') return  t.done;
      return true;
    })
    .filter(t => {
      if (priorityFilter === 'all') return true;
      return normP(t.priority) === priorityFilter;
    })
    .filter(t => {
      if (urgencyFilter === 'all') return true;
      const days = getDaysRemaining(t.created_at);
      if (urgencyFilter === 'overdue') return days !== null && days <= 0;
      if (urgencyFilter === 'urgent')  return days !== null && days > 0 && days <= 30;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'urgency') {
        const da = getDaysRemaining(a.created_at) ?? 9999;
        const db = getDaysRemaining(b.created_at) ?? 9999;
        return da - db;
      }
      const pa = PRIORITY_ORDER[a.priority] ?? 1;
      const pb = PRIORITY_ORDER[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      const da = a.due ? new Date(a.due) : new Date('9999-12-31');
      const db = b.due ? new Date(b.due) : new Date('9999-12-31');
      return da - db;
    });

  const openCount = tasks.filter(t => !t.done).length;
  const doneCount = tasks.filter(t => t.done).length;

  async function handleAdd() {
    if (!form.name.trim()) return;
    setSaving(true);
    await addTask({ ...form });
    setForm({ name: '', description: '', domain: 'AC', priority: 'high', owner: '', due: '' });
    setSaving(false);
    setShowModal(false);
  }

  const inputStyle = {
    width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)',
    borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', marginBottom: 5,
    display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  const filterBtnStyle = (active) => ({
    fontSize: 11, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
    fontFamily: 'var(--font)', fontWeight: active ? 500 : 400,
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border2)'}`,
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? '#fff' : 'var(--text2)', transition: 'all 0.15s',
  });

  return (
    <div className="view-pad" style={{ padding: 28, overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 16,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Remediation Tasks</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {openCount} open · {doneCount} completed
          </div>
        </div>
        <button onClick={() => setShowModal(true)} style={{
          fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
          cursor: 'pointer', border: '1px solid var(--accent)',
          background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
        }}>+ New Task</button>
      </div>

      {/* Empty state — no tasks yet */}
      {tasks.length === 0 && (
        <EmptyState
          icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>}
          title="No remediation tasks yet"
          description="Create tasks to track control gaps and assign ownership across your team."
          ctaLabel="Create Task"
          onCta={() => setShowModal(true)}
        />
      )}

      {/* All done state */}
      {tasks.length > 0 && tasks.every(t => t.done) && (
        <EmptyState
          icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
          title="All tasks complete"
          description="Every remediation task has been marked done. Your team is on track."
          accent="var(--green)"
        />
      )}

      {/* Overdue banner */}
      {(() => {
        if (tasks.length === 0 || tasks.every(t => t.done)) return null;
        const overdueCount = tasks.filter(t => {
          if (t.done) return false;
          const days = getDaysRemaining(t.created_at);
          return days !== null && days <= 0;
        }).length;
        if (overdueCount === 0) return null;
        return (
          <div style={{
            background: 'var(--red-bg)', border: '1px solid var(--red)',
            borderRadius: 8, padding: '12px 16px', marginBottom: 16,
            fontSize: 12, color: 'var(--red)', lineHeight: 1.5,
          }}>
            <strong>{overdueCount} item{overdueCount !== 1 ? 's' : ''} are overdue.</strong>
            {' '}CMMC Conditional Certification requires all POA&M items resolved within 180 days.
          </div>
        );
      })()}

      {/* Filters + table — only shown when there are tasks */}
      {tasks.length > 0 && <div>
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <span style={{ fontSize: 11, color: 'var(--text3)', marginRight: 4 }}>Status:</span>
        {['all', 'open', 'completed'].map(s => (
          <button key={s} style={filterBtnStyle(statusFilter === s)}
            onClick={() => setStatusFilter(s)}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8, marginRight: 4 }}>Priority:</span>
        {['all', 'high', 'medium', 'low'].map(p => (
          <button key={p} style={filterBtnStyle(priorityFilter === p)}
            onClick={() => setPriorityFilter(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8, marginRight: 4 }}>180-Day:</span>
        {[
          { key: 'all',     label: 'All'          },
          { key: 'overdue', label: 'Overdue'       },
          { key: 'urgent',  label: 'Urgent (<30d)' },
        ].map(u => (
          <button key={u.key} style={filterBtnStyle(urgencyFilter === u.key)}
            onClick={() => setUrgencyFilter(u.key)}>
            {u.label}
          </button>
        ))}
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8, marginRight: 4 }}>Sort:</span>
        {[
          { key: 'priority', label: 'Priority'    },
          { key: 'urgency',  label: 'Most Urgent' },
        ].map(s => (
          <button key={s.key} style={filterBtnStyle(sortBy === s.key)}
            onClick={() => setSortBy(s.key)}>
            {s.label}
          </button>
        ))}
      </div>

      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20,
      }}>
        <div className="tasks-table-scroll">
        <div style={{
          display: 'grid', gridTemplateColumns: '32px 1fr 90px 100px 100px 80px 90px 36px',
          gap: 8, paddingBottom: 10, borderBottom: '1px solid var(--border)',
          fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em',
        }}>
          <div></div>
          <div>Task</div>
          <div>Control</div>
          <div>Priority</div>
          <div>Due Date</div>
          <div>Status</div>
          <div>180-Day</div>
          <div></div>
        </div>

        {filtered.length === 0 && (
          <div style={{
            padding: '28px 0', textAlign: 'center',
            fontSize: 12, color: 'var(--text3)',
          }}>
            No tasks match the current filters.
          </div>
        )}

        {filtered.map(t => {
          const p = normP(t.priority);
          return (
            <div key={t.id} style={{
              display: 'grid', gridTemplateColumns: '32px 1fr 90px 100px 100px 80px 90px 36px',
              gap: 8, padding: '11px 0', borderBottom: '1px solid var(--border)',
              alignItems: 'center', fontSize: 12,
            }}>
              <div onClick={() => toggleTask(t.id)} style={{
                width: 18, height: 18, borderRadius: 4, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1.5px solid ${t.done ? 'var(--green)' : 'var(--border2)'}`,
                background: t.done ? 'var(--green)' : 'transparent',
                fontSize: 10, color: '#fff', fontWeight: 700, transition: 'all 0.15s',
              }}>{t.done ? '✓' : ''}</div>

              <div>
                <div style={{
                  fontWeight: 500,
                  textDecoration: t.done ? 'line-through' : 'none',
                  opacity: t.done ? 0.45 : 1,
                }}>{t.name}</div>
                {t.description && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                    {t.description}
                  </div>
                )}
                {t.owner && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
                    Owner: {t.owner}
                  </div>
                )}
              </div>

              <div style={{
                fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{t.domain || '—'}</div>

              <div>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                  fontFamily: 'var(--mono)', textTransform: 'uppercase',
                  background: priorityBg(t.priority), color: priorityColor(t.priority),
                }}>{p}</span>
              </div>

              <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--mono)' }}>
                {t.due || '—'}
              </div>

              <div>
                <span style={{
                  fontSize: 10, padding: '3px 8px', borderRadius: 10,
                  background: t.done ? 'var(--green-bg)' : 'var(--blue-bg)',
                  color: t.done ? 'var(--green)' : 'var(--accent)',
                }}>{t.done ? 'Complete' : 'Open'}</span>
              </div>

              <div>
                {!t.done && <PoamBadge createdAt={t.created_at} />}
              </div>

              <div
                onClick={() => deleteTask(t.id)}
                title="Delete task"
                style={{
                  width: 28, height: 28, borderRadius: 6, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: 15, color: 'var(--text3)',
                  transition: 'color 0.15s, background 0.15s',
                }}
                onMouseEnter={ev => { ev.currentTarget.style.color = 'var(--red)'; ev.currentTarget.style.background = 'var(--red-bg)'; }}
                onMouseLeave={ev => { ev.currentTarget.style.color = 'var(--text3)'; ev.currentTarget.style.background = 'transparent'; }}
              >×</div>
            </div>
          );
        })}
        </div>{/* end tasks-table-scroll */}
      </div>
      </div>}

      {/* New Task Modal */}
      {showModal && (
        <div
          className="modal-backdrop"
          onClick={e => e.target === e.currentTarget && setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div className="modal-box glass-panel" style={{
            padding: 24, width: 500, maxWidth: '95vw',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 18 }}>
              New Remediation Task
            </div>

            {/* Title */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Task Title *</label>
              <input
                type="text"
                placeholder="e.g. Deploy MFA on all admin accounts"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Description</label>
              <textarea
                placeholder="Describe what needs to be done and why..."
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>

            {/* Control Domain + Priority */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Control Domain</label>
                <select
                  value={form.domain}
                  onChange={e => setForm(prev => ({ ...prev, domain: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  {['AC','AU','CM','IA','IR','MA','MP','PS','PE','RA','CA','SC','SI','PM'].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Priority</label>
                <select
                  value={form.priority}
                  onChange={e => setForm(prev => ({ ...prev, priority: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {/* Assigned To + Due Date */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>Assigned To</label>
                <input
                  type="text"
                  placeholder="e.g. IT Team"
                  value={form.owner}
                  onChange={e => setForm(prev => ({ ...prev, owner: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
              <div>
                <label style={labelStyle}>Due Date</label>
                <input
                  type="date"
                  value={form.due}
                  onChange={e => setForm(prev => ({ ...prev, due: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{
                fontSize: 12, fontWeight: 500, padding: '8px 16px', borderRadius: 6,
                cursor: 'pointer', border: '1px solid var(--border2)',
                background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
              }}>Cancel</button>
              <button
                onClick={handleAdd}
                disabled={saving || !form.name.trim()}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '8px 20px', borderRadius: 6,
                  cursor: saving || !form.name.trim() ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--accent)', opacity: saving || !form.name.trim() ? 0.65 : 1,
                  background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
                }}
              >{saving ? 'Saving…' : 'Create Task'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Evidence({ session, framework: currentFramework, company, profile }) {
  const [evidenceList, setEvidenceList]   = useState([]);
  const [listLoading, setListLoading]     = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [saving, setSaving]               = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragging, setDragging]           = useState(false);
  const [search, setSearch]               = useState('');
  const [filterFramework, setFilterFramework] = useState('all');
  const [filterStatus, setFilterStatus]   = useState('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  const [modalError, setModalError]       = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [form, setForm] = useState({
    title: '', controlId: '', framework: currentFramework || 'cmmc_l2',
    notes: '', status: 'pending', file: null, fileType: '', fileSize: 0, fileName: '',
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!session?.user?.id) { setListLoading(false); return; }
    getEvidence(session.user.id).then(data => {
      setEvidenceList(data || []);
      setListLoading(false);
    });
  }, [session]);


  const EV_FRAMEWORKS = [
    { id: 'cmmc_l1',  label: 'CMMC Level 1'      },
    { id: 'cmmc_l2',  label: 'CMMC Level 2'      },
    { id: 'cmmc_l3',  label: 'CMMC Level 3'      },
    { id: 'nist_171', label: 'NIST SP 800-171'   },
    { id: 'soc2',     label: 'SOC 2'             },
    { id: 'iso27001', label: 'ISO 27001'          },
    { id: 'hipaa',    label: 'HIPAA'             },
    { id: 'nist_csf', label: 'NIST CSF 2.0'      },
    { id: 'pci_dss',  label: 'PCI DSS v4.0'      },
    { id: 'fedramp',  label: 'FedRAMP Moderate'  },
  ];
  const FW_LABEL = EV_FRAMEWORKS.reduce((acc, f) => { acc[f.id] = f.label; return acc; }, {});

  function fileTypeMeta(ft) {
    const t = (ft || '').toLowerCase();
    if (t.includes('pdf'))                                            return { label: 'PDF', bg: 'rgba(224,82,82,0.15)',   color: '#E05252' };
    if (t.includes('word') || t.includes('doc'))                    return { label: 'DOC', bg: 'rgba(45,125,210,0.15)',  color: '#2D7DD2' };
    if (t.includes('sheet') || t.includes('excel') || t === 'sheet') return { label: 'XLS', bg: 'rgba(29,184,122,0.15)', color: '#1DB87A' };
    if (t.includes('image') || t.includes('png') || t.includes('jpg') || t === 'img') return { label: 'IMG', bg: 'rgba(245,166,35,0.15)', color: '#F5A623' };
    if (t.includes('zip') || t.includes('compress'))                return { label: 'ZIP', bg: 'rgba(124,58,237,0.15)', color: '#7C3AED' };
    return { label: 'FILE', bg: 'rgba(90,100,120,0.15)', color: '#8A95A8' };
  }

  function formatBytes(bytes) {
    if (!bytes) return '—';
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  }

  function statusStyle(s) {
    const v = (s || '').toLowerCase();
    if (v === 'approved')                 return { bg: 'var(--green-bg)', color: 'var(--green)', label: 'Approved' };
    if (v === 'rejected')                 return { bg: 'var(--red-bg)',   color: 'var(--red)',   label: 'Rejected' };
    if (v === 'review' || v === 'in review') return { bg: 'var(--amber-bg)', color: 'var(--amber)', label: 'In Review' };
    if (v === 'draft')                    return { bg: 'var(--surface2)', color: 'var(--text3)', label: 'Draft' };
    return { bg: 'var(--blue-bg)', color: 'var(--accent)', label: 'Pending' };
  }

  function handleFileSelect(file) {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { setModalError('File exceeds the 25 MB limit.'); return; }
    const ok = /\.(pdf|doc|docx|xls|xlsx|png|jpg|jpeg|zip)$/i.test(file.name);
    if (!ok) { setModalError('Unsupported file type. Please use PDF, Word, Excel, PNG, JPG, or ZIP.'); return; }
    setModalError('');
    setForm(prev => ({
      ...prev, file, fileType: file.type, fileSize: file.size, fileName: file.name,
      title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
    }));
  }

  async function handleSubmit() {
    if (!form.title.trim()) { setModalError('Please enter a title.'); return; }
    // Trial upload cap
    const activeTrial = profile?.is_trial && !isTrialExpired(profile?.trial_started_at) && !profile?.trial_converted;
    if (activeTrial && evidenceList.length >= TRIAL_LIMITS.evidence_uploads) {
      setModalError(`Trial accounts are limited to ${TRIAL_LIMITS.evidence_uploads} evidence uploads. Upgrade to add more.`);
      return;
    }
    setSaving(true);
    setUploadProgress(0);
    setModalError('');

    let fileUrl = null;
    let fileType = form.fileType || null;
    let fileSize = form.fileSize || null;

    if (form.file && session?.user?.id) {
      setUploadProgress(25);
      const result = await uploadEvidenceFile(session.user.id, form.file);
      if (result.error) {
        setModalError('File upload failed: ' + result.error);
        setSaving(false); setUploadProgress(0); return;
      }
      fileUrl  = result.path;
      fileType = result.fileType;
      fileSize = result.fileSize;
      setUploadProgress(65);
    }

    const newEv = await createEvidence(session.user.id, {
      title:       form.title.trim(),
      control_id:  form.controlId.trim() || null,
      framework:   form.framework,
      notes:       form.notes.trim() || null,
      file_url:    fileUrl,
      file_type:   fileType,
      file_size:   fileSize,
      status:      form.status || 'pending',
      uploaded_by: session?.user?.email || null,
    });

    setUploadProgress(100);
    if (newEv) {
      setEvidenceList(prev => [newEv, ...prev]);
      if (session?.user?.id && company?.id) {
        logActivity(session.user.id, company.id, 'evidence_uploaded', 'evidence', { file_name: form.title.trim() || form.fileName });
      }
    }
    resetForm();
    setSaving(false); setUploadProgress(0); setShowModal(false);
  }

  function resetForm() {
    setForm({ title: '', controlId: '', framework: currentFramework || 'cmmc_l2', notes: '', status: 'pending', file: null, fileType: '', fileSize: 0, fileName: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleDownload(ev) {
    if (!ev.file_url) return;
    setDownloadError('');
    setDownloadingId(ev.id);
    const { signedUrl, error } = await getEvidenceDownloadUrl(ev.file_url);
    setDownloadingId(null);
    if (error || !signedUrl) {
      setDownloadError('Could not generate download link. Please try again.');
      return;
    }
    window.open(signedUrl, '_blank');
  }

  async function handleDelete(ev) {
    await deleteEvidence(ev.id, ev.file_url || null);
    setEvidenceList(prev => prev.filter(e => e.id !== ev.id));
    setConfirmDeleteId(null);
  }

  async function handleStatusChange(ev, newStatus) {
    setStatusUpdatingId(ev.id);
    await updateEvidenceStatus(ev.id, newStatus);
    setEvidenceList(prev => prev.map(e => e.id === ev.id ? { ...e, status: newStatus } : e));
    setStatusUpdatingId(null);
  }

  const filtered = evidenceList.filter(ev => {
    const title   = (ev.title || ev.name || '').toLowerCase();
    const control = (ev.control_id || ev.control || '').toLowerCase();
    if (search && !title.includes(search.toLowerCase()) && !control.includes(search.toLowerCase())) return false;
    if (filterFramework !== 'all' && ev.framework && ev.framework !== filterFramework) return false;
    if (filterStatus !== 'all') {
      const s = (ev.status || 'pending').toLowerCase();
      if (filterStatus !== s && !(filterStatus === 'review' && s === 'in review')) return false;
    }
    return true;
  });

  // Stats computed from evidenceList
  const totalCount    = evidenceList.length;
  const pendingCount  = evidenceList.filter(e => (e.status || 'pending').toLowerCase() === 'pending').length;
  const approvedCount = evidenceList.filter(e => (e.status || '').toLowerCase() === 'approved').length;
  const totalBytes    = evidenceList.reduce((sum, e) => sum + (e.file_size || 0), 0);
  const storageLabel  = totalBytes > 1024 * 1024 ? `${(totalBytes / 1024 / 1024).toFixed(1)} MB`
                      : totalBytes > 0 ? `${Math.round(totalBytes / 1024)} KB` : '0 KB';

  const inputStyle = {
    width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)',
    borderRadius: 6, padding: '9px 12px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', marginBottom: 5,
    display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  return (
    <div className="view-pad" style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Evidence Locker</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            Upload and manage documents that prove your security controls are implemented
          </div>
        </div>
        <button
          onClick={() => { setShowModal(true); setModalError(''); resetForm(); }}
          style={{
            fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
          }}
        >+ Add Evidence</button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Total Files',    value: totalCount,    color: 'var(--text)',    sub: 'uploaded'           },
          { label: 'Pending Review', value: pendingCount,  color: 'var(--amber)',   sub: 'awaiting approval'  },
          { label: 'Approved',       value: approvedCount, color: 'var(--green)',   sub: 'verified evidence'  },
          { label: 'Storage Used',   value: storageLabel,  color: 'var(--accent)',  sub: 'across all files'   },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '14px 16px',
          }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Download error banner */}
      {downloadError && (
        <div style={{
          background: 'var(--red-bg)', border: '1px solid var(--red)',
          borderRadius: 6, padding: '9px 14px', fontSize: 12, color: 'var(--red)',
          marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          {downloadError}
          <button onClick={() => setDownloadError('')} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by title or control ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: '1 1 200px', background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 6, padding: '7px 12px', fontSize: 12, color: 'var(--text)',
            fontFamily: 'var(--font)', outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--border2)'}
        />
        <select
          value={filterFramework}
          onChange={e => setFilterFramework(e.target.value)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 6, padding: '7px 10px', fontSize: 12, color: 'var(--text)',
            fontFamily: 'var(--font)', outline: 'none', cursor: 'pointer',
          }}
        >
          <option value="all">All Frameworks</option>
          {EV_FRAMEWORKS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 6, padding: '7px 10px', fontSize: 12, color: 'var(--text)',
            fontFamily: 'var(--font)', outline: 'none', cursor: 'pointer',
          }}
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="draft">Draft</option>
          <option value="review">In Review</option>
        </select>
      </div>

      {/* Evidence list */}
      {listLoading ? (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <div className="skeleton" style={{ width: 32, height: 32, borderRadius: 6, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="skeleton" style={{ height: 12, width: '45%', marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 10, width: '30%' }} />
              </div>
              <div className="skeleton" style={{ height: 10, width: 80 }} />
              <div className="skeleton" style={{ height: 10, width: 80 }} />
              <div className="skeleton" style={{ height: 22, width: 70, borderRadius: 10 }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 && evidenceList.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1.5px dashed var(--border2)',
          borderRadius: 12, padding: '48px 32px', textAlign: 'center',
        }}>
          <div style={{ marginBottom: 12, opacity: 0.4, display: 'flex', justifyContent: 'center' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text3)' }}>
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
              </svg>
            </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>No evidence uploaded yet</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 500, margin: '0 auto 20px', lineHeight: 1.7 }}>
            Evidence files are documents that prove your security controls are implemented —
            policies, procedures, screenshots, configurations, and audit logs.
            During a CMMC assessment, your C3PAO will review these to verify compliance.
          </div>
          <button
            onClick={() => { setShowModal(true); setModalError(''); }}
            style={{
              fontSize: 12, fontWeight: 600, padding: '9px 20px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--accent)',
              background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
            }}
          >Upload Your First Evidence File</button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '28px 20px', textAlign: 'center',
          fontSize: 12, color: 'var(--text3)',
        }}>
          No evidence matches your filters.
        </div>
      ) : (
        <div className="evidence-table-wrap" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '40px 1fr 120px 110px 90px 80px 100px 80px',
            gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)',
            fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em',
            minWidth: 740,
          }}>
            <div></div><div>Evidence</div><div>Control</div><div>Framework</div>
            <div>Date</div><div>Size</div><div>Status</div><div></div>
          </div>

          {filtered.map(ev => {
            const sc      = statusStyle(ev.status);
            const title   = ev.title || ev.name || 'Untitled';
            const control = ev.control_id || ev.control || '—';
            const fwLabel = FW_LABEL[ev.framework] || (ev.framework ? ev.framework : '—');
            const dateStr = ev.created_at
              ? new Date(ev.created_at).toLocaleDateString()
              : ev.date || '—';
            const size    = ev.file_size ? formatBytes(ev.file_size) : (ev.size || '—');
            const isDel   = confirmDeleteId === ev.id;

            return (
              <div key={ev.id} style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 120px 110px 90px 80px 100px 80px',
                gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)',
                alignItems: 'center', fontSize: 12, minWidth: 740,
                background: isDel ? 'var(--red-bg)' : 'transparent',
                transition: 'background 0.15s',
              }}>
                {(() => { const m = fileTypeMeta(ev.file_type || ev.type); return (
                  <div style={{
                    width: 32, height: 32, borderRadius: 6, background: m.bg,
                    border: `1px solid ${m.color}44`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 9, fontWeight: 700,
                    color: m.color, fontFamily: 'var(--mono)', flexShrink: 0,
                  }}>
                    {m.label}
                  </div>
                ); })()}
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text)' }}>{title}</div>
                  {ev.uploaded_by && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
                      {ev.uploaded_by}
                    </div>
                  )}
                  {ev.notes && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
                      {ev.notes.length > 60 ? ev.notes.slice(0, 60) + '…' : ev.notes}
                    </div>
                  )}
                  {ev.expires_at && (() => {
                    const daysLeft = Math.floor((new Date(ev.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
                    const expired  = daysLeft < 0;
                    const soon     = !expired && daysLeft <= 30;
                    if (!expired && !soon) return null;
                    return (
                      <span style={{
                        display: 'inline-block', marginTop: 3,
                        fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
                        padding: '1px 6px', borderRadius: 4,
                        background: expired ? 'var(--red-bg)' : 'var(--amber-bg)',
                        color:      expired ? 'var(--red)'    : 'var(--amber)',
                        border:     `1px solid ${expired ? 'var(--red)' : 'var(--amber)'}44`,
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>
                        {expired ? 'Expired' : 'Expires soon'}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>
                  {control}
                </div>
                <div>
                  <span style={{
                    fontSize: 10, padding: '2px 7px', borderRadius: 4,
                    background: 'var(--blue-bg)', color: 'var(--accent)',
                    border: '1px solid rgba(45,125,210,0.2)', whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}>
                    {fwLabel}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{dateStr}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{size}</div>
                <div>
                  <select
                    value={ev.status || 'pending'}
                    disabled={statusUpdatingId === ev.id}
                    onChange={e => handleStatusChange(ev, e.target.value)}
                    style={{
                      fontSize: 10, fontWeight: 600, padding: '3px 7px', borderRadius: 10,
                      background: sc.bg, color: sc.color, border: `1px solid ${sc.color}44`,
                      cursor: statusUpdatingId === ev.id ? 'wait' : 'pointer',
                      fontFamily: 'var(--font)', outline: 'none', appearance: 'none',
                      paddingRight: 16,
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="in_review">In Review</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {ev.file_url && (
                    <button
                      onClick={() => handleDownload(ev)}
                      disabled={downloadingId === ev.id}
                      title="Download file"
                      style={{
                        width: 28, height: 28, borderRadius: 5, border: '1px solid var(--border2)',
                        background: 'var(--surface)', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 13,
                        color: downloadingId === ev.id ? 'var(--text3)' : 'var(--text2)',
                        fontFamily: 'var(--font)',
                      }}
                    >↓</button>
                  )}
                  {isDel ? (
                    <div style={{ display: 'flex', gap: 3 }}>
                      <button
                        onClick={() => handleDelete(ev)}
                        style={{
                          fontSize: 10, padding: '3px 7px', borderRadius: 4, cursor: 'pointer',
                          border: '1px solid var(--red)', background: 'var(--red)',
                          color: '#fff', fontFamily: 'var(--font)',
                        }}
                      >Yes</button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        style={{
                          fontSize: 10, padding: '3px 7px', borderRadius: 4, cursor: 'pointer',
                          border: '1px solid var(--border2)', background: 'var(--surface)',
                          color: 'var(--text2)', fontFamily: 'var(--font)',
                        }}
                      >No</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(ev.id)}
                      title="Delete"
                      style={{
                        width: 28, height: 28, borderRadius: 5, border: '1px solid var(--border)',
                        background: 'transparent', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 14,
                        color: 'var(--text3)', fontFamily: 'var(--font)', transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; e.currentTarget.style.background = 'var(--red-bg)'; e.currentTarget.style.borderColor = 'var(--red)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                    >×</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showModal && (
        <div
          onClick={e => e.target === e.currentTarget && !saving && setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div className="modal-box glass-panel" style={{
            padding: 28, width: 520, maxWidth: '100%',
            maxHeight: 'calc(100vh - 40px)', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Add Evidence</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
              Upload a document that proves a security control is implemented.
            </div>

            {/* File drop zone */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>File (Optional — 25 MB max)</label>
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handleFileSelect(f); }}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragging ? 'var(--accent)' : form.file ? 'var(--green)' : 'var(--border2)'}`,
                  borderRadius: 8, padding: '18px 16px', textAlign: 'center',
                  cursor: 'pointer',
                  background: dragging ? 'rgba(45,125,210,0.05)' : form.file ? 'var(--green-bg)' : 'var(--surface)',
                  transition: 'all 0.2s',
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.zip"
                  style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
                />
                {form.file ? (
                  <div>
                    {(() => { const m = fileTypeMeta(form.fileType); return (
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, background: m.bg,
                        border: `1px solid ${m.color}55`, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 10, fontWeight: 700,
                        color: m.color, fontFamily: 'var(--mono)', margin: '0 auto 6px',
                      }}>{m.label}</div>
                    ); })()}
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--green)' }}>{form.fileName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{formatBytes(form.fileSize)} — click to change</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: 6, opacity: 0.4, display: 'flex', justifyContent: 'center' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                      </svg>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>Drop file here or click to browse</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>PDF, Word, Excel, PNG, JPG, ZIP</div>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Title *</label>
              <input
                type="text"
                placeholder="e.g. MFA Policy Document, Network Diagram 2026"
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>

            {/* Control ID + Framework */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Control ID (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. AC.L2-3.1.1"
                  value={form.controlId}
                  onChange={e => setForm(prev => ({ ...prev, controlId: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
              <div>
                <label style={labelStyle}>Framework</label>
                <select
                  value={form.framework}
                  onChange={e => setForm(prev => ({ ...prev, framework: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  {EV_FRAMEWORKS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
            </div>

            {/* Status */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Status</label>
              <select
                value={form.status}
                onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="pending">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="in_review">In Review</option>
              </select>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Notes (Optional)</label>
              <textarea
                placeholder="What does this evidence demonstrate? Any relevant context..."
                value={form.notes}
                onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Uploading… {uploadProgress}%</div>
                <div style={{ height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'var(--accent)', borderRadius: 2, transition: 'width 0.3s' }} />
                </div>
              </div>
            )}

            {modalError && (
              <div style={{
                background: 'var(--red-bg)', border: '1px solid var(--red)',
                borderRadius: 6, padding: '10px 12px', fontSize: 12,
                color: 'var(--red)', marginBottom: 14,
              }}>{modalError}</div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowModal(false); resetForm(); setModalError(''); }}
                disabled={saving}
                style={{
                  fontSize: 12, fontWeight: 500, padding: '8px 16px', borderRadius: 6,
                  cursor: saving ? 'not-allowed' : 'pointer', border: '1px solid var(--border2)',
                  background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
                }}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={saving || !form.title.trim()}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '8px 20px', borderRadius: 6,
                  cursor: saving || !form.title.trim() ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--accent)', background: 'var(--accent)',
                  color: '#fff', fontFamily: 'var(--font)',
                  opacity: saving || !form.title.trim() ? 0.65 : 1,
                }}
              >
                {saving ? (form.file ? 'Uploading…' : 'Saving…') : 'Save Evidence'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const REPORT_TITLES = {
  cmmc_l1: 'System Security Plan (SSP)',
  cmmc_l2: 'System Security Plan (SSP)',
  cmmc_l3: 'System Security Plan (SSP)',
  nist_171: 'NIST SP 800-171 Assessment Report',
  soc2:    'SOC 2 Audit Readiness Report',
  iso27001:'ISO 27001 Statement of Applicability',
  hipaa:   'HIPAA Risk Analysis Report',
  nist_csf:'NIST CSF 2.0 Assessment Report',
  pci_dss: 'PCI DSS v4.0 Compliance Report',
  fedramp: 'FedRAMP Moderate Assessment Report',
};

function Report({ level, answers, profile, framework, onExportSSP, onExportSSPDocx, docxLoading, onExportPOAM, session, shareLinks, onCreateShareLink, onToggleShareLink, onDeleteShareLink, onDownloadAuditTrail }) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareForm, setShareForm] = useState({ label: 'Compliance Report', showControls: true, showEvidence: true, showTasks: false, showScore: true, expiryDays: 30, auditorEmail: '' });
  const [generatingLink, setGeneratingLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState(null);
  const [copiedLink, setCopiedLink]     = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [auditorEmailSent, setAuditorEmailSent] = useState(false);
  const isCmmc    = ['cmmc_l1','cmmc_l2','cmmc_l3'].includes(framework);
  const questions = getQuestionsByLevel(level, framework);
  const today     = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const metCount   = questions.filter(q => answers[q.id] === 'yes').length;
  const totalCount = questions.length;
  const pctScore   = totalCount > 0 ? Math.round((metCount / totalCount) * 100) : 0;

  // Real gaps from answers
  const gaps = questions
    .filter(q => answers[q.id] === 'no' || answers[q.id] === 'partial')
    .map(q => ({
      id:       q.id,
      req:      q.text.length > 55 ? q.text.substring(0, 55) + '…' : q.text,
      sub:      q.domain + ' Domain',
      priority: answers[q.id] === 'no' ? 'high' : 'med',
    }))
    .sort((a) => (a.priority === 'high' ? -1 : 1));

  // Domain data from answers
  const domainMap = {};
  questions.forEach(q => {
    if (!domainMap[q.domain]) domainMap[q.domain] = { id: q.domain, name: q.domain, met: 0, total: 0 };
    domainMap[q.domain].total++;
    if (answers[q.id] === 'yes') domainMap[q.domain].met++;
  });
  const domains = Object.values(domainMap).map(d => ({
    ...d, pct: d.total > 0 ? Math.round((d.met / d.total) * 100) : 0,
  })).sort((a, b) => a.id.localeCompare(b.id));

  const docTitle = REPORT_TITLES[framework] || 'Compliance Assessment Report';

  const sysInfoRows = isCmmc ? [
    { key: 'Organization',    val: profile?.company_name || 'Your Company' },
    { key: 'CAGE Code',       val: profile?.cage_code    || 'Not Set'      },
    { key: 'CMMC Target',     val: `Level ${level}`                         },
    { key: 'Assessment Date', val: today                                    },
    { key: 'SPRS Score',      val: `${calculateSPRSScore ? calculateSPRSScore(answers) : '—'} / 110` },
    { key: 'Readiness Score', val: `${pctScore}%`                           },
  ] : [
    { key: 'Organization',    val: profile?.company_name || 'Your Company' },
    { key: 'Framework',       val: docTitle                                 },
    { key: 'Assessment Date', val: today                                    },
    { key: 'Compliance Score', val: `${pctScore}%`                         },
    { key: 'Controls Met',    val: `${metCount} of ${totalCount}`           },
  ];

  return (
    <div className="view-pad" style={{ padding: 28, overflowY: 'auto', height: '100%' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{docTitle}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            Auto-generated from your assessment data
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onDownloadAuditTrail && (
            <button onClick={onDownloadAuditTrail} style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
            }}>Download Audit Trail</button>
          )}
          <button onClick={() => setShowShareModal(true)} style={{
            fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
            border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
          }}>Share Report</button>
          <button onClick={onExportPOAM} style={{
            fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
            border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
          }}>Export POA&M</button>
          <button onClick={onExportSSP} style={{
            fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
            border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
          }}>Export PDF</button>
          <button onClick={onExportSSPDocx} disabled={docxLoading} style={{
            fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
            cursor: docxLoading ? 'not-allowed' : 'pointer',
            border: '1px solid var(--accent)', background: 'transparent',
            color: 'var(--accent)', fontFamily: 'var(--font)',
            opacity: docxLoading ? 0.6 : 1,
          }}>{docxLoading ? 'Generating…' : 'Export Word'}</button>
        </div>
      </div>

      {/* Score widget */}
      {isCmmc && (
        <div style={{ marginBottom: 16 }}>
          <SPRSScore answers={answers} />
        </div>
      )}
      {!isCmmc && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '18px 24px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 20,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
            background: 'var(--blue-bg)', border: `3px solid var(--accent)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{pctScore}%</span>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Compliance Score</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              {metCount} of {totalCount} controls implemented · {gaps.length} gaps identified
            </div>
          </div>
        </div>
      )}

      {/* System Info */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, marginBottom: 14,
          paddingBottom: 10, borderBottom: '1px solid var(--border)',
        }}>
          System Information
        </div>
        {sysInfoRows.map(r => (
          <div key={r.key} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 12,
          }}>
            <span style={{ color: 'var(--text3)' }}>{r.key}</span>
            <span style={{ fontWeight: 500 }}>{r.val}</span>
          </div>
        ))}
      </div>

      {/* POA&M */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, marginBottom: 14,
          paddingBottom: 10, borderBottom: '1px solid var(--border)',
        }}>
          Plan of Action and Milestones (POA&M)
          <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400, marginLeft: 8 }}>
            {gaps.filter(g => g.priority === 'high').length} high priority
          </span>
        </div>
        {gaps.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)', padding: '12px 0' }}>
            No gaps found. Complete your assessment to see findings here.
          </div>
        ) : (
          <>
            <div className="report-table-wrap">
          <div style={{
              display: 'grid', gridTemplateColumns: '130px 1fr 90px 100px',
              gap: 10, paddingBottom: 10, borderBottom: '1px solid var(--border)',
              fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em',
            }}>
              <div>Control ID</div><div>Finding</div><div>Priority</div><div>Target Date</div>
            </div>
            {gaps.map(g => (
              <div key={g.id} style={{
                display: 'grid', gridTemplateColumns: '130px 1fr 90px 100px',
                gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)',
                alignItems: 'center', fontSize: 12,
              }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{g.id}</div>
                <div>
                  <div style={{ fontWeight: 500 }}>{g.req}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{g.sub}</div>
                </div>
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                    fontFamily: 'var(--mono)', textTransform: 'uppercase',
                    background: priorityBg(g.priority), color: priorityColor(g.priority),
                  }}>{g.priority}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--mono)' }}>Q3 2026</div>
              </div>
            ))}
          </div>{/* end report-table-wrap */}
          </>
        )}
      </div>

      {/* Domain Summary */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, marginBottom: 14,
          paddingBottom: 10, borderBottom: '1px solid var(--border)',
        }}>
          Domain Summary
        </div>
        {domains.map(d => (
          <div key={d.id} style={{
            display: 'grid', gridTemplateColumns: '160px 1fr 50px 90px',
            gap: 12, alignItems: 'center', padding: '9px 0',
            borderBottom: '1px solid var(--border)', fontSize: 12,
          }}>
            <div>
              <span style={{ fontWeight: 500 }}>{d.name}</span>
              <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginLeft: 6 }}>{d.id}</span>
            </div>
            <div style={{ height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${d.pct}%`, background: statusColor(d.pct), borderRadius: 2 }} />
            </div>
            <div style={{ fontWeight: 600, color: statusColor(d.pct) }}>{d.pct}%</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{d.met}/{d.total} ctrl</div>
          </div>
        ))}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: '28px 32px', maxWidth: 520, width: '90%', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Share Compliance Report</div>
            {!newLinkUrl ? (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>LINK LABEL</label>
                  <input value={shareForm.label} onChange={e => setShareForm(f => ({ ...f, label: e.target.value }))} style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--text)', fontFamily: 'var(--font)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>SHOW IN REPORT</div>
                  {[
                    { key: 'showControls', label: 'Control Status' },
                    { key: 'showEvidence', label: 'Evidence List' },
                    { key: 'showTasks',    label: 'Remediation Tasks' },
                    { key: 'showScore',    label: 'Readiness Score' },
                  ].map(opt => (
                    <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 6 }}>
                      <input type="checkbox" checked={shareForm[opt.key]} onChange={e => setShareForm(f => ({ ...f, [opt.key]: e.target.checked }))} />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>EXPIRES IN</label>
                  <select value={shareForm.expiryDays} onChange={e => setShareForm(f => ({ ...f, expiryDays: Number(e.target.value) }))} style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: '7px 12px', fontSize: 13, color: 'var(--text)', fontFamily: 'var(--font)' }}>
                    <option value={7}>7 days</option>
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                    <option value={365}>1 year</option>
                  </select>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>EMAIL TO AUDITOR <span style={{ color: 'var(--text3)', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                  <input
                    type="email"
                    placeholder="auditor@c3pao.com"
                    value={shareForm.auditorEmail}
                    onChange={e => setShareForm(f => ({ ...f, auditorEmail: e.target.value }))}
                    style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--text)', fontFamily: 'var(--font)', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={async () => {
                    setGeneratingLink(true);
                    setAuditorEmailSent(false);
                    const expiresAt = new Date(Date.now() + shareForm.expiryDays * 24 * 60 * 60 * 1000).toISOString();
                    const link = await onCreateShareLink({ ...shareForm, framework, expiresAt });
                    if (link) {
                      const linkUrl = `https://app.theclearaudit.com/report/${link.token}`;
                      setNewLinkUrl(linkUrl);
                      // Send auditor invite email if address was provided
                      if (shareForm.auditorEmail && shareForm.auditorEmail.includes('@')) {
                        try {
                          await supabase.functions.invoke('send-auditor-invite', {
                            body: {
                              auditorEmail: shareForm.auditorEmail,
                              companyName:  profile?.company_name || 'TheClearAudit User',
                              inviterName:  profile?.company_name || session?.user?.email || 'TheClearAudit User',
                              accessLink:   linkUrl,
                              expiresAt:    new Date(expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                              framework:    framework ? framework.toUpperCase().replace('_', ' ') : 'CMMC Level 2',
                            },
                          });
                          setAuditorEmailSent(true);
                        } catch (_) { /* non-fatal */ }
                      }
                    }
                    setGeneratingLink(false);
                  }} disabled={generatingLink} style={{ flex: 1, padding: '10px', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)' }}>
                    {generatingLink ? 'Generating...' : 'Generate Link'}
                  </button>
                  <button onClick={() => setShowShareModal(false)} style={{ padding: '10px 18px', borderRadius: 7, fontSize: 13, cursor: 'pointer', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text2)', fontFamily: 'var(--font)' }}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ background: 'var(--green-bg)', border: '1px solid var(--green)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, marginBottom: 8 }}>
                    Link generated successfully!{auditorEmailSent ? ' Invite sent to auditor.' : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--text)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{newLinkUrl}</div>
                    <button onClick={() => { navigator.clipboard.writeText(newLinkUrl); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }} style={{ padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border2)', background: copiedLink ? 'var(--green-bg)' : 'var(--surface)', color: copiedLink ? 'var(--green)' : 'var(--text2)', fontFamily: 'var(--font)' }}>
                      {copiedLink ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                {/* Existing links */}
                {shareLinks && shareLinks.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>All Links</div>
                    {shareLinks.slice(0, 5).map(sl => (
                      <div key={sl.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                        <span style={{ flex: 1, color: 'var(--text)' }}>{sl.label}</span>
                        <span style={{ color: 'var(--text3)', fontSize: 11 }}>{sl.view_count || 0} views</span>
                        <span style={{ color: 'var(--text3)', fontSize: 11 }}>exp {new Date(sl.expires_at).toLocaleDateString()}</span>
                        <button
                          onClick={() => {
                            const url = `https://app.theclearaudit.com/report/${sl.token}`;
                            navigator.clipboard.writeText(url);
                            setCopiedLinkId(sl.id);
                            setTimeout(() => setCopiedLinkId(null), 2000);
                          }}
                          style={{ fontSize: 10, padding: '3px 7px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${copiedLinkId === sl.id ? 'var(--green)' : 'var(--border2)'}`, background: copiedLinkId === sl.id ? 'var(--green-bg)' : 'transparent', color: copiedLinkId === sl.id ? 'var(--green)' : 'var(--text2)', fontFamily: 'var(--font)', transition: 'all 0.15s' }}
                        >{copiedLinkId === sl.id ? 'Copied!' : 'Copy Link'}</button>
                        <button onClick={() => onToggleShareLink && onToggleShareLink(sl.id, !sl.is_active)} style={{ fontSize: 10, padding: '3px 7px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${sl.is_active ? 'var(--green)' : 'var(--border2)'}`, background: sl.is_active ? 'var(--green-bg)' : 'transparent', color: sl.is_active ? 'var(--green)' : 'var(--text3)', fontFamily: 'var(--font)' }}>{sl.is_active ? 'Active' : 'Off'}</button>
                        <button onClick={() => onDeleteShareLink && onDeleteShareLink(sl.id)} style={{ fontSize: 10, padding: '3px 7px', borderRadius: 4, cursor: 'pointer', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--red)', fontFamily: 'var(--font)' }}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={() => { setShowShareModal(false); setNewLinkUrl(null); setAuditorEmailSent(false); setShareForm({ label: 'Compliance Report', showControls: true, showEvidence: true, showTasks: false, showScore: true, expiryDays: 30, auditorEmail: '' }); }} style={{ width: '100%', padding: '10px', borderRadius: 7, fontSize: 13, cursor: 'pointer', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text2)', fontFamily: 'var(--font)' }}>Close</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function JoinCompanyPage({ token, session, onAccepted, onDecline }) {
  const [invite, setInvite]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    lookupCompanyInviteByToken(token).then(data => {
      setInvite(data);
      setLoading(false);
    });
  }, [token]);

  async function handleAccept() {
    setAccepting(true);
    setError('');
    const result = await acceptCompanyInviteByToken(token, session.user.id);
    if (result.success) {
      onAccepted(result.companyName);
    } else {
      setError(result.error || 'Failed to accept invite.');
      setAccepting(false);
    }
  }

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading invite...</div>
    </div>
  );

  if (!invite) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Invite Not Found</div>
      <div style={{ fontSize: 13, color: 'var(--text3)', maxWidth: 360, textAlign: 'center' }}>
        This invite link is invalid, has already been used, or has expired.
      </div>
      <button onClick={onDecline} style={{
        marginTop: 8, padding: '8px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600,
        cursor: 'pointer', border: '1px solid var(--border2)', background: 'transparent',
        color: 'var(--text2)', fontFamily: 'var(--font)',
      }}>
        Continue to App
      </button>
    </div>
  );

  const expires = new Date(invite.expires_at);
  const daysLeft = Math.max(0, Math.ceil((expires - Date.now()) / 86400000));

  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: 24,
    }}>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 14, maxWidth: 420, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 28px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--blue-bg)',
          textAlign: 'center',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, margin: '0 auto 12px',
            background: 'linear-gradient(135deg, #2D7DD2, #0D3F73)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            You've been invited
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            Join your team on TheClearAudit
          </div>
        </div>

        {/* Details */}
        <div style={{ padding: '24px 28px' }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 10, padding: '16px 18px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Company</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
                  {invite.companies?.name || 'Unknown Company'}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                    background: 'var(--blue-bg)', color: 'var(--accent)',
                    border: '1px solid rgba(45,125,210,0.25)',
                    fontFamily: 'var(--mono)', textTransform: 'uppercase',
                  }}>
                    {invite.role}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                    Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div style={{ marginBottom: 14, fontSize: 12, color: 'var(--red)', padding: '8px 12px', background: 'var(--red-bg)', borderRadius: 6 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onDecline}
              style={{
                flex: 1, padding: '10px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', border: '1px solid var(--border2)', background: 'transparent',
                color: 'var(--text2)', fontFamily: 'var(--font)',
              }}
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={accepting}
              style={{
                flex: 2, padding: '10px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                cursor: accepting ? 'default' : 'pointer',
                border: '1px solid var(--accent)', background: 'var(--accent)',
                color: '#fff', fontFamily: 'var(--font)', opacity: accepting ? 0.7 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              {accepting ? 'Joining…' : `Join ${invite.companies?.name || 'Team'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateCompanyModal({ session, profile, userPlan, onCreated, onSkip }) {
  const [name, setName]       = useState(profile?.company_name || '');
  const [creating, setCreating] = useState(false);
  const [error, setError]     = useState('');
  const seatLimit = userPlan === 'professional' ? 3 : userPlan === 'business' ? Infinity : 1;

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Enter a company name.'); return; }
    setCreating(true);
    setError('');
    const { data: co, error: err } = await supabase
      .from('companies')
      .insert({ owner_id: session.user.id, name: name.trim() })
      .select()
      .single();
    if (err) { setError('Failed to create team. Try again.'); setCreating(false); return; }
    onCreated(co);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 16, padding: '32px 32px 26px', width: '100%', maxWidth: 440,
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
      }}>
        <div style={{ fontSize: 24, marginBottom: 10 }}>🏢</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Set up your team account</div>
        <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 24, lineHeight: 1.6 }}>
          Your <strong style={{ color: 'var(--accent)' }}>{userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}</strong> plan includes{' '}
          <strong>{seatLimit === Infinity ? 'unlimited' : seatLimit} seat{seatLimit !== 1 ? 's' : ''}</strong>.
          Invite your team and collaborate on shared compliance data.
        </div>
        {error && (
          <div style={{ background: 'var(--red-bg)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 7, padding: '9px 12px', fontSize: 12, color: 'var(--red)', marginBottom: 14 }}>{error}</div>
        )}
        <form onSubmit={handleCreate}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>Company Name *</label>
            <input
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }}
              value={name} onChange={e => setName(e.target.value)} placeholder="Acme Defense LLC" required autoFocus
            />
          </div>
          <button type="submit" disabled={creating} style={{ width: '100%', padding: '12px 0', borderRadius: 9, fontSize: 14, fontWeight: 700, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', opacity: creating ? 0.7 : 1, marginBottom: 10 }}>
            {creating ? 'Creating…' : 'Create Team Account'}
          </button>
        </form>
        <button onClick={onSkip} style={{ width: '100%', padding: '9px 0', background: 'none', border: 'none', color: 'var(--text3)', fontSize: 13, cursor: 'pointer' }}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

function showToast(message, type = 'info') {
  window.dispatchEvent(new CustomEvent('theclearaudit-toast', { detail: { message, type } }));
}

export default function App() {
  const [view, setView]               = useState(() => sessionStorage.getItem('currentView') || 'dashboard');
  const [level, setLevel]             = useState(2);
  const [session, setSession]         = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showLogin, setShowLogin]         = useState(false);
  const [showSignUp, setShowSignUp]       = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [profile, setProfile]         = useState({});
  const [answers, setAnswers]         = useState({});
  const [tasks, setTasks]             = useState([]);
  const [evidence, setEvidence]       = useState([]);
  const [auditorToken, setAuditorToken] = useState(null);
  const [showTerms, setShowTerms]         = useState(false);
  const [showPrivacy, setShowPrivacy]     = useState(false);
  const [showSecurity, setShowSecurity]   = useState(false);
  const [showDPA, setShowDPA]             = useState(false);
  const [showAUP, setShowAUP]             = useState(false);
  const [showSLA, setShowSLA]             = useState(false);
  const [showCUI, setShowCUI]             = useState(false);
  const [framework, setFramework]       = useState('cmmc_l2');
  const [isAdminUser, setIsAdminUser]   = useState(false);
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [company, setCompany]           = useState(null);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [pendingInvite, setPendingInvite] = useState(null);
  const [showInviteBanner, setShowInviteBanner] = useState(false);
  const [memberFrameworks, setMemberFrameworks] = useState([]);
  const [showJoinSuccess, setShowJoinSuccess]   = useState(false);
  const [joinedCompanyName, setJoinedCompanyName] = useState('');
  const [joinToken, setJoinToken]               = useState(null);
  const [agencyJoinToken, setAgencyJoinToken]   = useState(null);
  const [agencyTab, setAgencyTab]               = useState('overview');
  const [viewingAsClient, setViewingAsClient]   = useState(null); // { client, agency }
  const [companyOwnerPlan, setCompanyOwnerPlan] = useState(null);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);

  // Trial gate modal state
  const [trialGate, setTrialGate]               = useState(null); // { feature, key }

  // Phase 2B state
  const [notifications, setNotifications]       = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [narratives, setNarratives]             = useState([]);
  const [narrativeDraft, setNarrativeDraft]     = useState({});
  const [docxLoading, setDocxLoading]           = useState(false);
  const [shareLinks, setShareLinks]             = useState([]);
  const [controlFilters, setControlFilters]     = useState({ statuses: [], domain: '', hasEvidence: false, hasNarrative: false });
  const narrativeTimers                         = useRef({});

  // Derived: owner status and role for the current user within their company
  const isCompanyOwner = !!(company && session && company.owner_id === session.user.id);
  const companyMemberRecord = companyMembers.find(m => m.user_id === session?.user?.id);
  const companyRole = isCompanyOwner ? 'owner' : (companyMemberRecord?.role || null);

  // Effective plan: non-owner members inherit the company owner's plan.
  // Active trial users are treated as 'professional' so PlanGate lets them through;
  // TrialGate then blocks specific features per TRIAL_LIMITS.
  const isActiveTrial = !!(profile?.is_trial && !isTrialExpired(profile?.trial_started_at) && !profile?.trial_converted);
  const effectivePlan = normalizePlan(
    (companyRole && companyRole !== 'owner' && companyOwnerPlan)
      ? companyOwnerPlan
      : isActiveTrial
        ? 'professional'
        : profile?.plan
  );

  // Compliance user ID: non-owner members use owner's data
  const complianceUserId = (companyRole && companyRole !== 'owner' && company?.owner_id)
    ? company.owner_id
    : session?.user?.id;

  // ── document.title per view ──────────────────────────────────────────────────
  useEffect(() => {
    const titles = {
      dashboard:       'Dashboard',
      assessment:      'Assessment',
      gaps:            'Gap Analysis',
      tasks:           'Remediation Tasks',
      evidence:        'Evidence Locker',
      report:          'Reports & SSP',
      account:         'Account Settings',
      profile:         'Profile',
      billing:         'Billing',
      auditor:         'Auditor Access',
      affirmation:     'Annual Affirmation',
      policies:        'Policy Library',
      frameworks:      'All Frameworks',
      sspReader:       'SSP Reader',
      agency:          'Agency Dashboard',
      companySettings: 'Company Settings',
      team:            'My Team',
    };
    document.title = `TheClearAudit — ${titles[view] || 'Dashboard'}`;
    sessionStorage.setItem('currentView', view);
  }, [view]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setShowSuccess(true);
    }

    const tokenParam = params.get('auditor');
    if (tokenParam) {
      setAuditorToken(tokenParam);
    }

    // Agency client invite token
    const joinParam = params.get('join');
    if (joinParam) {
      setAgencyJoinToken(joinParam);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Persist company invite token through auth redirects
    const companyInviteParam = params.get('company_invite') || null;
    if (companyInviteParam) {
      sessionStorage.setItem('pending_company_invite', companyInviteParam);
      window.history.replaceState({}, '', window.location.pathname);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthChecked(true);
      if (session) {
        // Restore invite token saved before auth redirect
        const savedToken = sessionStorage.getItem('pending_company_invite');
        if (savedToken) {
          sessionStorage.removeItem('pending_company_invite');
          setJoinToken(savedToken);
        }
        loadAllData(session.user.id, session.user.email);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // Do not log the user in — show the reset form instead
          setShowPasswordReset(true);
          return;
        }
        setSession(session);
        if (session) {
          const savedToken = sessionStorage.getItem('pending_company_invite');
          if (savedToken) {
            sessionStorage.removeItem('pending_company_invite');
            setJoinToken(savedToken);
          }
          loadAllData(session.user.id, session.user.email);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  async function loadAllData(userId, userEmail) {
    setLoading(true);

    // Admin dashboard hidden until needed — re-enable when customer base grows
    const [profileData, adminCheck] = await Promise.all([
      getProfile(userId),
      isUserAdmin(userId),
    ]);

    // Company + membership — determine complianceUserId before fetching compliance data
    const companyData = await getOrCreateCompany(userId, profileData?.company_name || profileData?.full_name || 'My Company');
    setIsAdminUser(!!adminCheck);
    let resolvedComplianceId = userId;
    if (companyData) {
      setCompany(companyData);
      const members = await getCompanyMembers(companyData.id);
      setCompanyMembers(members);
      // Find current user's member record
      const myMembership = members.find(m => m.user_id === userId);
      if (myMembership && (myMembership.assigned_frameworks || []).length > 0) {
        setMemberFrameworks(myMembership.assigned_frameworks);
      }
      // If user is NOT the owner, use owner's data + plan
      if (companyData.owner_id !== userId) {
        resolvedComplianceId = companyData.owner_id;
        const ownerProfile = await getProfile(companyData.owner_id);
        setCompanyOwnerPlan(ownerProfile?.plan || null);
      }
    }

    const [answersData, tasksData, evidenceData, narrativesData, notificationsData] = await Promise.all([
      getAssessments(resolvedComplianceId),
      getTasks(resolvedComplianceId),
      getEvidence(resolvedComplianceId),
      getNarratives(resolvedComplianceId),
      getNotifications(userId),
    ]);
    // Check for pending invite for this user's email
    if (userEmail) {
      const invite = await checkPendingInvite(userEmail);
      if (invite) {
        setPendingInvite(invite);
        setShowInviteBanner(true);
      }
    }
    // ── Activate free trial for brand-new users ───────────────────────────
    // Triggered once: plan is null (never paid) and trial not yet started
    let activeProfile = profileData;
    if (profileData && !profileData.plan && !profileData.is_trial) {
      const trialFields = {
        is_trial:         true,
        trial_started_at: new Date().toISOString(),
        trial_plan:       'professional',
        plan_status:      'trial',
      };
      await saveProfile(userId, trialFields);
      activeProfile = { ...profileData, ...trialFields };
    }

    if (activeProfile) {
      setProfile(activeProfile);
      if (activeProfile.target_level) setLevel(activeProfile.target_level);
    }
    if (answersData && Object.keys(answersData).length > 0) {
      setAnswers(answersData);
    }
    if (tasksData && tasksData.length > 0) {
      setTasks(tasksData);
    }
    if (evidenceData && evidenceData.length > 0) {
      setEvidence(evidenceData);
    }
    if (narrativesData) setNarratives(narrativesData);
    if (notificationsData) setNotifications(notificationsData);
    // Generate session notifications after data loads
    if (tasksData && evidenceData) {
      generateSessionNotifications(userId, tasksData, evidenceData).then(() => {
        getNotifications(userId).then(n => { if (n) setNotifications(n); });
      });
    }
    // Load share links
    getShareLinks(userId).then(sl => { if (sl) setShareLinks(sl); });
    setLoading(false);
  }

  async function handleLogout() {
    setIsTransitioning(true);
    await new Promise(r => setTimeout(r, 250));
    await supabase.auth.signOut();
    setIsTransitioning(false);
    setSession(null);
    sessionStorage.removeItem('currentView');
    setView('dashboard');
    setAnswers({});
    setTasks([]);
    setEvidence([]);
    setProfile({});
    setIsAdminUser(false);
    setCompany(null);
    setCompanyMembers([]);
    setPendingInvite(null);
    setShowInviteBanner(false);
    setMemberFrameworks([]);
    setShowJoinSuccess(false);
    setJoinedCompanyName('');
    setNotifications([]);
    setNarratives([]);
    setNarrativeDraft({});
    setShareLinks([]);
  }

  async function setAnswer(id, val) {
    const prev = answers[id];
    setAnswers(prevA => ({ ...prevA, [id]: val }));
    if (session) {
      await saveAnswer(complianceUserId || session.user.id, id, val);
      if (prev !== val) {
        const userName = profile?.company_name || session.user.email || '';
        logControlChange(complianceUserId || session.user.id, company?.id || null, id, framework, prev || null, val, userName).catch(() => {});
        if (company?.id) {
          logActivity(session.user.id, company.id, 'control_updated', 'control', { control_id: id, old_status: prev || null, new_status: val });
        }
      }
    }
  }

  function handleNarrativeChange(controlId, text) {
    setNarrativeDraft(prev => ({ ...prev, [controlId]: text }));
    if (narrativeTimers.current[controlId]) clearTimeout(narrativeTimers.current[controlId]);
    narrativeTimers.current[controlId] = setTimeout(async () => {
      const uid = complianceUserId || session?.user?.id;
      if (!uid) return;
      const ok = await saveNarrative(uid, controlId, framework, text);
      if (ok) {
        setNarratives(prev => {
          const existing = prev.find(n => n.control_id === controlId && n.framework === framework);
          if (existing) return prev.map(n => n.control_id === controlId && n.framework === framework ? { ...n, narrative: text } : n);
          return [...prev, { control_id: controlId, framework, narrative: text, user_id: uid }];
        });
      }
    }, 2000);
  }

  function getNarrative(controlId) {
    if (narrativeDraft[controlId] !== undefined) return narrativeDraft[controlId];
    const saved = narratives.find(n => n.control_id === controlId && n.framework === framework);
    return saved?.narrative || '';
  }

  async function toggleTask(id) {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    const newDone = !task.done;
    setTasks(prev => prev.map(t => t.id === id ? { ...t, done: newDone } : t));
    await updateTask(id, { done: newDone });
  }

  async function removeTask(id) {
    setTasks(prev => prev.filter(t => t.id !== id));
    await dbDeleteTask(id);
  }

  async function addTask(form) {
    if (session) {
      const newTask = await createTask(complianceUserId || session.user.id, { ...form, done: false });
      if (newTask) setTasks(prev => [newTask, ...prev]);
    } else {
      setTasks(prev => [{ ...form, id: Date.now(), done: false }, ...prev]);
    }
  }


  async function handleSaveProfile(data) {
    setProfile(data);
    if (session) await saveProfile(session.user.id, data);
  }

  function showTrialGate(feature) {
    setTrialGate(prev => ({ feature, key: (prev?.key ?? 0) + 1 }));
  }

  async function handleExportSSP(qs) {
    if (isActiveTrial && TRIAL_LIMITS.ssp_pdf_export === false) { showTrialGate('ssp_pdf_export'); return; }
    const uid = complianceUserId || session?.user?.id;
    let history = [];
    let sprsHist = [];
    if (uid) {
      try { history = (await getFullControlHistory(uid) || []).slice(0, 50); } catch (e) { /* silent */ }
      if (['cmmc_l1','cmmc_l2','cmmc_l3','nist_171'].includes(framework)) {
        try {
          const { data } = await supabase.from('sprs_score_history').select('id, score, recorded_at, notes').eq('user_id', uid).order('recorded_at', { ascending: true }).limit(60);
          sprsHist = data || [];
        } catch (e) { /* silent */ }
      }
    }
    try {
      generateSSP(level, LEVELS[level], answers, qs, tasks, evidence, profile, framework, company || {}, narratives, history, sprsHist);
      if (session?.user?.id && company?.id) {
        logActivity(session.user.id, company.id, 'pdf_exported', 'document', { doc_type: 'SSP', framework });
      }
    } catch (err) {
      console.error('SSP PDF export failed:', err);
      showToast('Export failed — please try again or contact support.', 'error');
    }
  }

  async function handleExportSSPDocx(qs) {
    if (isActiveTrial && TRIAL_LIMITS.ssp_pdf_export === false) { showTrialGate('ssp_pdf_export'); return; }
    setDocxLoading(true);
    try {
      const uid = complianceUserId || session?.user?.id;
      let history = [];
      let sprsHist = [];
      if (uid) {
        try { history = (await getFullControlHistory(uid) || []).slice(0, 50); } catch (e) { /* silent */ }
        if (['cmmc_l1','cmmc_l2','cmmc_l3','nist_171'].includes(framework)) {
          try {
            const { data } = await supabase.from('sprs_score_history').select('id, score, recorded_at, notes').eq('user_id', uid).order('recorded_at', { ascending: true }).limit(60);
            sprsHist = data || [];
          } catch (e) { /* silent */ }
        }
      }
      await generateSSPDocx({ level, levelData: LEVELS[level], answers, questions: qs, tasks, evidence, profile, framework, company: company || {}, narratives, controlHistory: history, sprsHistory: sprsHist });
      if (session?.user?.id && company?.id) {
        logActivity(session.user.id, company.id, 'pdf_exported', 'document', { doc_type: 'SSP-DOCX', framework });
      }
    } catch (err) {
      console.error('DOCX export failed:', err);
      showToast('Export failed — please try again or contact support.', 'error');
    } finally {
      setDocxLoading(false);
    }
  }

  function handleExportPOAM() {
    if (isActiveTrial && TRIAL_LIMITS.poam_pdf_export === false) { showTrialGate('poam_pdf_export'); return; }
    try {
      generatePOAM(tasks, level, framework, profile);
      if (session?.user?.id && company?.id) {
        logActivity(session.user.id, company.id, 'pdf_exported', 'document', { doc_type: 'POA&M', framework });
      }
    } catch (err) {
      console.error('POA&M export failed:', err);
      showToast('Export failed — please try again or contact support.', 'error');
    }
  }

  function handleExportSpreadsheet() {
    if (isActiveTrial && TRIAL_LIMITS.spreadsheet_export === false) { showTrialGate('spreadsheet_export'); return; }
    try {
      generateSSPSpreadsheet(activeQuestions, answers, profile, tasks, evidence, framework, narratives, company);
    } catch (err) {
      console.error('Spreadsheet export failed:', err);
      showToast('Export failed — please try again or contact support.', 'error');
    }
  }

  async function handleApplySSPData({ controls, systemInfo }) {
    // Apply control answers (skip unknown)
    if (controls) {
      for (const [controlId, status] of Object.entries(controls)) {
        if (['yes', 'partial', 'no'].includes(status)) {
          await setAnswer(controlId, status);
        }
      }
    }
    // Merge extracted system info into profile
    if (systemInfo) {
      const updates = {};
      if (systemInfo.organizationName) updates.company_name = systemInfo.organizationName;
      if (systemInfo.cageCode)         updates.cage_code    = systemInfo.cageCode;
      if (Object.keys(updates).length > 0) {
        await handleSaveProfile({ ...profile, ...updates });
      }
    }
  }

  // Public report page — no auth required
  if (window.location.pathname.startsWith('/report/')) {
    return <PublicReport />;
  }

  if (!authChecked) return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)',
      color: 'var(--text3)', fontSize: 13,
    }}>
      Loading...
    </div>
  );

  if (showTerms) return (
    <TermsOfService onBack={() => setShowTerms(false)} />
  );

  if (showPrivacy) return (
    <PrivacyPolicy onBack={() => setShowPrivacy(false)} />
  );

  if (showSecurity) return (
    <SecurityPage onBack={() => setShowSecurity(false)} />
  );

  if (showDPA) return (
    <DataProcessingAgreement onBack={() => setShowDPA(false)} />
  );

  if (showAUP) return (
    <AcceptableUsePolicy onBack={() => setShowAUP(false)} />
  );

  if (showSLA) return (
    <ServiceLevelAgreement onBack={() => setShowSLA(false)} />
  );

  if (showCUI) return (
    <CUIPolicy onBack={() => setShowCUI(false)} />
  );

  if (auditorToken) return (
    <AuditorView token={auditorToken} />
  );

  if (showPasswordReset) return (
    <ResetPassword onDone={() => {
      setShowPasswordReset(false);
      setShowLogin(true);
    }} />
  );

  if (showLogin || showSignUp) return (
    <Login
      initialMode={showSignUp ? 'signup' : 'login'}
      onLogin={() => {
        supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
        setShowLogin(false);
        setShowSignUp(false);
      }}
      onBack={() => {
        setShowLogin(false);
        setShowSignUp(false);
      }}
    />
  );

if (!session) return (
    <Landing
      onSignUp={() => setShowSignUp(true)}
      onLogin={() => setShowLogin(true)}
      onTerms={() => setShowTerms(true)}
      onPrivacy={() => setShowPrivacy(true)}
      onSecurity={() => setShowSecurity(true)}
      onDPA={() => setShowDPA(true)}
      onAUP={() => setShowAUP(true)}
      onSLA={() => setShowSLA(true)}
      onCUI={() => setShowCUI(true)}
    />
  );

  if (showSuccess) return (
    <Success onContinue={() => {
      setShowSuccess(false);
      window.history.replaceState({}, '', '/');
    }} />
  );

  const isExpiredTrialNoPlan = !!(profile?.is_trial && isTrialExpired(profile?.trial_started_at) && !profile?.trial_converted && !profile?.plan);

  if (showPricing) return (
    <Pricing
      currentPlan={profile?.plan || null}
      session={session}
      profile={profile}
      onBack={isExpiredTrialNoPlan ? null : () => setShowPricing(false)}
      onPlanChange={async () => {
        const updatedProfile = await getProfile(session.user.id);
        if (updatedProfile) setProfile(updatedProfile);
        setShowPricing(false);
      }}
    />
  );

  if (loading) return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      {/* Sidebar skeleton */}
      <div style={{ width: 256, background: 'var(--bg2)', borderRight: '1px solid var(--border)', padding: 20, flexShrink: 0 }}>
        <div className="skeleton" style={{ height: 34, width: 140, marginBottom: 24 }} />
        <div className="skeleton" style={{ height: 56, borderRadius: 9, marginBottom: 16 }} />
        {[80, 60, 70, 60, 80, 60].map((w, i) => (
          <div key={i} className="skeleton" style={{ height: 32, width: `${w}%`, borderRadius: 7, marginBottom: 6 }} />
        ))}
      </div>
      {/* Main content skeleton */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Topbar skeleton */}
        <div style={{ height: 58, background: 'var(--bg2)', borderBottom: '1px solid var(--border)', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="skeleton" style={{ height: 24, width: 200 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="skeleton" style={{ height: 32, width: 90, borderRadius: 6 }} />
            <div className="skeleton" style={{ height: 32, width: 100, borderRadius: 6 }} />
          </div>
        </div>
        {/* Dashboard skeleton */}
        <div style={{ padding: 28, overflowY: 'auto', flex: 1 }}>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[1,2,3,4].map(i => (
              <div key={i} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '18px 20px' }}>
                <div className="skeleton" style={{ height: 10, width: '60%', marginBottom: 12 }} />
                <div className="skeleton" style={{ height: 32, width: '50%', marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 10, width: '70%' }} />
              </div>
            ))}
          </div>
          {/* Quick actions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
            {[1,2,3,4].map(i => (
              <div key={i} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                <div className="skeleton" style={{ height: 34, width: 34, borderRadius: 8, marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 12, width: '70%', marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 10, width: '80%' }} />
              </div>
            ))}
          </div>
          {/* Score card */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
            <div className="skeleton" style={{ height: 14, width: 200, marginBottom: 20 }} />
            {[1,2,3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
                <div className="skeleton" style={{ height: 12, flex: 1 }} />
                <div className="skeleton" style={{ height: 5, width: 160, borderRadius: 3 }} />
                <div className="skeleton" style={{ height: 12, width: 40 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  const userPlan = effectivePlan;

  const activeQuestions = getQuestionsByLevel(level, framework);

  const views = {
    dashboard:   <Dashboard level={level} answers={answers} framework={framework} evidenceCount={evidence.length} company={company} companyMembers={companyMembers} onViewCompanySettings={() => setView('companySettings')} onStartAssessment={() => setView('assessment')} onGoToEvidence={() => setView('evidence')} onGoToReport={() => setView('report')} onGoToTasks={() => setView('tasks')} tasks={tasks} evidence={evidence} affirmations={[]} narratives={narratives} session={session} setView={setView} />,
    assessment:  assessmentStarted
                   ? <Assessment answers={answers} setAnswer={setAnswer} level={level} framework={framework} onBack={() => setAssessmentStarted(false)} session={session} userPlan={userPlan} onUpgrade={() => setShowPricing(true)} getNarrative={getNarrative} handleNarrativeChange={handleNarrativeChange} narratives={narratives} evidence={evidence} controlFilters={controlFilters} setControlFilters={setControlFilters} />
                   : <AssessmentIntro
                       answers={answers}
                       framework={framework}
                       level={level}
                       onStart={() => setAssessmentStarted(true)}
                       onReset={() => {
                         const qs = getQuestionsByLevel(level, framework);
                         setAnswers(prev => {
                           const next = { ...prev };
                           qs.forEach(q => { delete next[q.id]; });
                           return next;
                         });
                         setAssessmentStarted(true);
                       }}
                     />,
    gaps:        <GapAnalysis
                   answers={answers}
                   questions={activeQuestions}
                   onExportPOAM={handleExportPOAM}
                   tasks={tasks}
                   evidence={evidence}
                   addTask={addTask}
                   setView={setView}
                   framework={framework}
                   profile={profile}
                   userPlan={userPlan}
                   isActiveTrial={isActiveTrial}
                 />,
    tasks:       <Tasks
                   tasks={tasks}
                   toggleTask={toggleTask}
                   deleteTask={removeTask}
                   addTask={addTask}
                 />,
    evidence:    <Evidence session={session} framework={framework} company={company} profile={profile} />,
    report:      <Report
                   level={level}
                   answers={answers}
                   profile={profile}
                   framework={framework}
                   onExportSSP={() => handleExportSSP(activeQuestions)}
                   onExportSSPDocx={() => handleExportSSPDocx(activeQuestions)}
                   docxLoading={docxLoading}
                   onExportPOAM={handleExportPOAM}
                   session={session}
                   shareLinks={shareLinks}
                   onCreateShareLink={async (opts) => {
                     const link = await createShareLink(complianceUserId || session.user.id, opts);
                     if (link) setShareLinks(prev => [link, ...prev]);
                     return link;
                   }}
                   onToggleShareLink={async (id, isActive) => {
                     await updateShareLink(id, { is_active: isActive });
                     setShareLinks(prev => prev.map(sl => sl.id === id ? { ...sl, is_active: isActive } : sl));
                   }}
                   onDeleteShareLink={async (id) => {
                     await deleteShareLink(id);
                     setShareLinks(prev => prev.filter(sl => sl.id !== id));
                   }}
                   onDownloadAuditTrail={async () => {
                     const history = await getFullControlHistory(complianceUserId || session.user.id);
                     // Build audit trail export using xlsx
                     const XLSX = await import('xlsx');
                     const rows = [['Date', 'Control ID', 'Changed By', 'From', 'To', 'Notes']];
                     history.forEach(h => {
                       rows.push([
                         new Date(h.created_at).toLocaleString(),
                         h.control_id,
                         h.changed_by_name || '',
                         h.old_status || '—',
                         h.new_status || '—',
                         h.notes || '',
                       ]);
                     });
                     const ws = XLSX.utils.aoa_to_sheet(rows);
                     const wb = XLSX.utils.book_new();
                     XLSX.utils.book_append_sheet(wb, ws, 'Audit Trail');
                     XLSX.writeFile(wb, `control-audit-trail-${new Date().toISOString().split('T')[0]}.xlsx`);
                   }}
                 />,
    account:     <AccountSettings
                   session={session}
                   profile={profile}
                   currentPlan={userPlan}
                   onSave={handleSaveProfile}
                   onUpgrade={() => setShowPricing(true)}
                 />,
    profile:     <Profile
                   session={session}
                   profile={profile}
                   onSave={handleSaveProfile}
                 />,
    billing:     (companyRole && companyRole !== 'owner')
                   ? (
                     <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                       <div style={{ textAlign: 'center', maxWidth: 360 }}>
                         <div style={{
                           width: 56, height: 56, borderRadius: 14, margin: '0 auto 16px',
                           background: 'var(--surface)', border: '1px solid var(--border)',
                           display: 'flex', alignItems: 'center', justifyContent: 'center',
                           color: 'var(--text3)',
                         }}>
                           <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                             <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                           </svg>
                         </div>
                         <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Billing Restricted</div>
                         <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.65 }}>
                           Only the account owner can manage billing and subscription settings.
                         </div>
                       </div>
                     </div>
                   )
                   : <Billing
                       session={session}
                       currentPlan={userPlan}
                       onUpgrade={() => setShowPricing(true)}
                       onDPA={() => setShowDPA(true)}
                     />,
    auditor:     <PlanGate userPlan={userPlan} requiredPlan="professional" onUpgrade={() => setShowPricing(true)} featureName="Auditor Access">
                   <AuditorAccess session={session} />
                 </PlanGate>,
    affirmation: <PlanGate userPlan={userPlan} requiredPlan="professional" onUpgrade={() => setShowPricing(true)} featureName="Annual Affirmation Tracker">
                   <Affirmation
                     session={session}
                     answers={answers}
                     level={level}
                     profile={profile}
                   />
                 </PlanGate>,
    policies:    <PlanGate userPlan={userPlan} requiredPlan="professional" onUpgrade={() => setShowPricing(true)} featureName="Policy Template Library">
                   <PolicyLibrary profile={profile} onUpgrade={() => setShowPricing(true)} />
                 </PlanGate>,
    frameworks: <FrameworkSelector
                  currentFramework={framework}
                  userPlan={userPlan}
                  allowedFrameworks={memberFrameworks}
                  onSelect={id => {
                    setFramework(id);
                    const lvMap = { cmmc_l1: 1, cmmc_l2: 2, cmmc_l3: 3 };
                    setLevel(lvMap[id] ?? 2);
                    setAssessmentStarted(false); // reset intro when framework changes
                    setView('assessment');
                  }}
                  onUpgrade={() => setShowPricing(true)}
                />,
    sspReader: <PlanGate userPlan={userPlan} requiredPlan="professional" onUpgrade={() => setShowPricing(true)} featureName="SSP Reader">
                 <SSPReader
                   session={session}
                   answers={answers}
                   setAnswer={setAnswer}
                   onApplyData={handleApplySSPData}
                   userPlan={userPlan}
                   onUpgrade={() => setShowPricing(true)}
                 />
               </PlanGate>,
    agency: isAgency ? (
              <AgencyDashboard
                session={session}
                profile={profile}
                activeTab={agencyTab}
                onSetTab={(t) => setAgencyTab(t)}
                onWhiteLabelSave={(whiteLabelData) => {
                  setProfile(prev => ({ ...prev, ...whiteLabelData }));
                }}
              />
            ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text3)', fontSize: 13 }}>Agency features are not available on your plan.</div>,
    companySettings: <CompanySettings
                       company={company}
                       companyMembers={companyMembers}
                       session={session}
                       profile={profile}
                       userPlan={userPlan}
                       onUpdate={() => {
                         if (session?.user?.id) {
                           getOrCreateCompany(session.user.id, company?.name || profile?.company_name || 'My Company')
                             .then(co => { if (co) setCompany(co); });
                         }
                       }}
                     />,
    team: <TeamManagement
                       session={session}
                       profile={profile}
                       companyId={company?.id || null}
                       effectivePlan={effectivePlan}
                       companyName={company?.name || profile?.company_name || ''}
                       onUpgrade={() => setShowPricing(true)}
                     />,
    // Admin dashboard hidden until needed — re-enable when customer base grows
    admin: false
      ? <AdminDashboard session={session} />
      : <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100%', flexDirection: 'column', gap: 12,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'var(--red-bg)', border: '1px solid var(--red)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--red)',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>403 — Access Denied</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>You do not have permission to view this page.</div>
        </div>,
  };

  // ── Agency Client Invite Page ──────────────────────────────────────────────
  if (agencyJoinToken) {
    return (
      <JoinAgency
        token={agencyJoinToken}
        onDone={() => { setAgencyJoinToken(null); }}
        onLogin={() => setAgencyJoinToken(null)}
      />
    );
  }

  // ── Join Company Page ──────────────────────────────────────────────────────
  if (joinToken && session) {
    return (
      <JoinCompanyPage
        token={joinToken}
        session={session}
        onAccepted={async (companyName) => {
          setJoinToken(null);
          setJoinedCompanyName(companyName);
          setShowJoinSuccess(true);
          // Reload company data after joining
          await loadAllData(session.user.id, session.user.email);
        }}
        onDecline={() => setJoinToken(null)}
      />
    );
  }

  return (
    <ErrorBoundary>
      <Toast />
    <div className="fade-transition" style={{ display: 'flex', height: '100vh', overflow: 'hidden', opacity: isTransitioning ? 0 : 1, transition: 'opacity 0.25s ease' }}>
      {/* Company Creation Modal */}
      {showCreateCompanyModal && session && (
        <CreateCompanyModal
          session={session}
          profile={profile}
          userPlan={userPlan}
          onCreated={async (co) => {
            setCompany(co);
            const members = await getCompanyMembers(co.id);
            setCompanyMembers(members);
            setShowCreateCompanyModal(false);
            setView('companySettings');
          }}
          onSkip={() => {
            localStorage.setItem('skipCreateCompany', Date.now() + 30 * 24 * 60 * 60 * 1000);
            setShowCreateCompanyModal(false);
          }}
        />
      )}
      {/* ── Guided onboarding flow — shown once to new users ── */}
      {session && profile && (isActiveTrial || effectivePlan) && (
        <OnboardingFlow session={session} profile={profile} setView={setView} />
      )}

      {/* ── Trial gate modal (feature-specific upgrade prompt) ── */}
      {trialGate && (
        <TrialGate
          key={trialGate.key}
          feature={trialGate.feature}
          profile={profile}
          onUpgrade={() => { setShowPricing(true); setTrialGate(null); }}
        >
          <div />
        </TrialGate>
      )}

      {isExpiredTrialNoPlan && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <div style={{
            background: 'var(--bg2)', borderRadius: 16, padding: '48px 40px', maxWidth: 460, width: '90%',
            textAlign: 'center', border: '1px solid var(--border2)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14, margin: '0 auto 20px',
              background: 'var(--amber-bg)', border: '1px solid var(--amber)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--amber)',
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
              Your free trial has ended
            </div>
            <div style={{ fontSize: 14, color: 'var(--text3)', lineHeight: 1.65, marginBottom: 28 }}>
              Your 7-day free trial has expired. Choose a plan to continue accessing
              TheClearAudit and keep all of your assessment progress.
            </div>
            <button
              onClick={() => setShowPricing(true)}
              style={{
                width: '100%', padding: '14px', borderRadius: 8, fontSize: 15,
                fontWeight: 700, cursor: 'pointer', border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
                marginBottom: 10,
              }}
            >
              Choose a Plan
            </button>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              Your data is saved and will be waiting for you after upgrading.
            </div>
          </div>
        </div>
      )}
      {viewingAsClient && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#f97316', color: '#fff',
          padding: '8px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', fontSize: 13, fontWeight: 600,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          <span>👁️ Viewing as client: <strong>{viewingAsClient.company_name || viewingAsClient.client_name}</strong></span>
          <button
            onClick={() => setViewingAsClient(null)}
            style={{ background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
          >
            ← Return to Agency
          </button>
        </div>
      )}
      <Sidebar
        view={view}
        setView={(v) => { setView(v); setSidebarOpen(false); }}
        onLogout={handleLogout}
        profile={profile}
        session={session}
        framework={framework}
        onUpgrade={() => setShowPricing(true)}
        isAdminUser={isAdminUser}
        company={company}
        companyMembers={companyMembers}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCompanyOwner={isCompanyOwner}
        companyRole={companyRole}
        agencyTab={agencyTab}
        onSetAgencyTab={(t) => { setAgencyTab(t); setView('agency'); setSidebarOpen(false); }}
        companyOwnerPlan={companyOwnerPlan}
        effectivePlan={effectivePlan}
      />
      <div className="app-main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, position: 'relative' }}>
        <Topbar
          view={view}
          level={level}
          framework={framework}
          onAddEvidence={() => setView('evidence')}
          onUpgrade={() => setShowPricing(true)}
          onExportSSP={() => handleExportSSP(activeQuestions)}
          onExportSSPDocx={() => handleExportSSPDocx(activeQuestions)}
          docxLoading={docxLoading}
          onExportPOAM={handleExportPOAM}
          onExportSpreadsheet={handleExportSpreadsheet}
          isAdminUser={isAdminUser}
          setView={setView}
          onMenuToggle={() => setSidebarOpen(o => !o)}
          userPlan={userPlan}
          notifications={notifications}
          showNotifications={showNotifications}
          onToggleNotifications={() => setShowNotifications(v => !v)}
          onMarkNotificationRead={async (id) => {
            await markNotificationRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setShowNotifications(false);
          }}
          onMarkAllRead={async () => {
            const uid = session?.user?.id;
            if (uid) {
              await markAllNotificationsRead(uid);
              setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            }
          }}
        />
        <TrialBanner profile={profile} onUpgrade={() => setShowPricing(true)} />
        {showJoinSuccess && (
          <div style={{
            background: 'var(--green-bg)', borderBottom: '1px solid rgba(29,184,122,0.25)',
            padding: '10px 28px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 16, flexShrink: 0,
          }}>
            <div style={{ fontSize: 12, color: 'var(--text)' }}>
              You have joined <strong>{joinedCompanyName}</strong>. Welcome to the team!
            </div>
            <button
              onClick={() => setShowJoinSuccess(false)}
              style={{
                fontSize: 12, padding: '4px 10px', borderRadius: 5, cursor: 'pointer',
                border: '1px solid rgba(29,184,122,0.4)', background: 'transparent',
                color: 'var(--green)', fontFamily: 'var(--font)',
              }}
            >
              Dismiss
            </button>
          </div>
        )}
        {showInviteBanner && pendingInvite && (
          <div style={{
            background: 'var(--blue-bg)', borderBottom: '1px solid rgba(45,125,210,0.25)',
            padding: '10px 28px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 16, flexShrink: 0,
          }}>
            <div style={{ fontSize: 12, color: 'var(--text)' }}>
              <strong>{pendingInvite.companies?.name || 'A company'}</strong> has invited you to join their team.
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={async () => {
                  const result = await acceptCompanyInvite(pendingInvite.invite_token, session.user.id);
                  if (result) {
                    setShowInviteBanner(false);
                    setPendingInvite(null);
                    setJoinedCompanyName(result.companies?.name || pendingInvite.companies?.name || 'your team');
                    setShowJoinSuccess(true);
                    const co = await getCompany(session.user.id);
                    if (co) {
                      setCompany(co);
                      const members = await getCompanyMembers(co.id);
                      setCompanyMembers(members);
                      const mine = members.find(m => m.user_id === session.user.id);
                      if (mine && (mine.assigned_frameworks || []).length > 0) {
                        setMemberFrameworks(mine.assigned_frameworks);
                      }
                    }
                  }
                }}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 6,
                  cursor: 'pointer', border: '1px solid var(--accent)',
                  background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
                }}
              >
                Accept Invite
              </button>
              <button
                onClick={() => { setShowInviteBanner(false); setPendingInvite(null); }}
                style={{
                  fontSize: 12, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                  border: '1px solid var(--border2)', background: 'transparent',
                  color: 'var(--text3)', fontFamily: 'var(--font)',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        <AnimatePresence mode="popLayout">
          <motion.div
            key={view}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            style={{ flex: 1, overflow: 'hidden' }}
          >
            {views[view]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
    </ErrorBoundary>
  );
}